import React, { useState, useEffect, useRef } from 'react';
import './GhostCamera.css';

// SVG Data URIs representing clinical "Before" and "After" mock images
// for fallback testing or demonstration.
const MOCK_BEFORE = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600">
  <rect width="800" height="600" fill="%23ffffff"/>
  <g stroke="%23e2e8f0" stroke-width="1" stroke-dasharray="5 5">
    <line x1="400" y1="0" x2="400" y2="600"/>
    <line x1="0" y1="300" x2="800" y2="300"/>
  </g>
  <path d="M 400 150 Q 520 150 520 280 Q 520 420 400 480 Q 280 420 280 280 Q 280 150 400 150" fill="none" stroke="%233b82f6" stroke-width="6"/>
  <ellipse cx="350" cy="250" rx="20" ry="10" fill="none" stroke="%233b82f6" stroke-width="4"/>
  <circle cx="350" cy="250" r="6" fill="%233b82f6"/>
  <ellipse cx="450" cy="250" rx="20" ry="10" fill="none" stroke="%233b82f6" stroke-width="4"/>
  <circle cx="450" cy="250" r="6" fill="%233b82f6"/>
  <path d="M 360 190 Q 400 180 440 190" fill="none" stroke="%233b82f6" stroke-width="3" stroke-linecap="round"/>
  <path d="M 400 250 L 400 320 L 415 320" fill="none" stroke="%233b82f6" stroke-width="4" stroke-linecap="round"/>
  <path d="M 360 380 Q 400 370 440 380 Q 400 395 360 380" fill="%233b82f6" opacity="0.6"/>
  <text x="50" y="80" fill="%23475569" font-family="sans-serif" font-size="28" font-weight="bold">BASELINE (BEFORE)</text>
</svg>`;

const MOCK_AFTER = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600">
  <rect width="800" height="600" fill="%23ffffff"/>
  <g stroke="%23e2e8f0" stroke-width="1" stroke-dasharray="5 5">
    <line x1="400" y1="0" x2="400" y2="600"/>
    <line x1="0" y1="300" x2="800" y2="300"/>
  </g>
  <path d="M 400 150 Q 520 150 520 280 Q 520 420 400 480 Q 280 420 280 280 Q 280 150 400 150" fill="none" stroke="%233b82f6" stroke-width="6"/>
  <ellipse cx="350" cy="250" rx="20" ry="10" fill="none" stroke="%233b82f6" stroke-width="4"/>
  <circle cx="350" cy="250" r="6" fill="%233b82f6"/>
  <ellipse cx="450" cy="250" rx="20" ry="10" fill="none" stroke="%233b82f6" stroke-width="4"/>
  <circle cx="450" cy="250" r="6" fill="%233b82f6"/>
  <path d="M 360 190 Q 400 180 440 190" fill="none" stroke="%233b82f6" stroke-width="3" stroke-linecap="round"/>
  <path d="M 400 250 L 400 320 L 415 320" fill="none" stroke="%233b82f6" stroke-width="4" stroke-linecap="round"/>
  <path d="M 360 380 Q 400 365 440 380 Q 400 405 360 380" fill="%2310b981" opacity="0.9"/>
  <text x="50" y="80" fill="%2310b981" font-family="sans-serif" font-size="28" font-weight="bold">POST-TREATMENT (AFTER)</text>
</svg>`;

const ZOOM_FACTOR = 1.25;

// Expression list constant
const EXPRESSIONS = [
  { id: 'at-rest', name: 'At rest', templateId: 'front' },
  { id: 'pursed-kiss', name: 'Pursed kiss', templateId: 'front' },
  { id: 'closed-smile', name: 'Closed big smile', templateId: 'front' },
  { id: 'grimace', name: 'Grimace', templateId: 'front' },
  { id: 'big-smile', name: 'Big smile', templateId: 'front' },
  { id: 'furrow', name: 'Furrow', templateId: 'front' }
];

const FACE_LANDMARK_GROUPS = [
  {
    id: 'face-oval',
    label: 'FACE_OVAL',
    color: '#2563eb',
    points: Array.from({ length: 36 }, (_, index) => {
      const angle = (-96 + index * (360 / 36)) * (Math.PI / 180);
      return [Math.cos(angle) * 50, Math.sin(angle) * 75];
    }),
    closed: true
  },
  {
    id: 'left-eyebrow-top',
    label: 'LEFT_EYEBROW_TOP',
    color: '#f97316',
    points: [[-42, -36], [-34, -43], [-22, -46], [-10, -44], [-2, -38]]
  },
  {
    id: 'left-eyebrow-bottom',
    label: 'LEFT_EYEBROW_BOTTOM',
    color: '#facc15',
    points: [[-39, -32], [-29, -35], [-17, -36], [-6, -34]]
  },
  {
    id: 'right-eyebrow-top',
    label: 'RIGHT_EYEBROW_TOP',
    color: '#22c55e',
    points: [[8, -38], [18, -44], [31, -46], [43, -43], [50, -36]]
  },
  {
    id: 'right-eyebrow-bottom',
    label: 'RIGHT_EYEBROW_BOTTOM',
    color: '#7c3aed',
    points: [[11, -34], [23, -36], [35, -35], [47, -32]]
  },
  {
    id: 'left-eye',
    label: 'LEFT_EYE',
    color: '#06b6d4',
    points: [[-41, -16], [-34, -22], [-24, -23], [-14, -20], [-8, -15], [-16, -10], [-27, -9], [-37, -11]],
    closed: true
  },
  {
    id: 'right-eye',
    label: 'RIGHT_EYE',
    color: '#f472b6',
    points: [[9, -15], [16, -20], [26, -23], [37, -22], [44, -16], [37, -11], [27, -9], [16, -10]],
    closed: true
  },
  {
    id: 'upper-lip-top',
    label: 'UPPER_LIP_TOP',
    color: '#84cc16',
    points: [[-31, 45], [-18, 36], [-6, 38], [0, 42], [7, 38], [20, 36], [32, 45]]
  },
  {
    id: 'upper-lip-bottom',
    label: 'UPPER_LIP_BOTTOM',
    color: '#ec4899',
    points: [[-28, 47], [-16, 44], [-6, 45], [0, 48], [7, 45], [18, 44], [29, 47]]
  },
  {
    id: 'lower-lip-top',
    label: 'LOWER_LIP_TOP',
    color: '#ef4444',
    points: [[-29, 50], [-18, 56], [-5, 58], [8, 58], [20, 56], [30, 50]]
  },
  {
    id: 'lower-lip-bottom',
    label: 'LOWER_LIP_BOTTOM',
    color: '#38bdf8',
    points: [[-27, 52], [-15, 68], [0, 73], [15, 68], [28, 52]]
  },
  {
    id: 'nose-bridge',
    label: 'NOSE_BRIDGE',
    color: '#a855f7',
    points: [[0, -23], [0, -9], [0, 7], [1, 23]]
  },
  {
    id: 'nose-bottom',
    label: 'NOSE_BOTTOM',
    color: '#14b8a6',
    points: [[-17, 27], [-8, 33], [1, 35], [12, 33], [20, 27]]
  }
];

function FaceDetectionOverlay({ facePosition }) {
  const centerX = facePosition.x;
  const centerY = facePosition.y;
  const faceBox = {
    x: centerX - 63,
    y: centerY - 97,
    width: 126,
    height: 180
  };
  const toPoint = ([x, y]) => `${centerX + x},${centerY + y}`;

  return (
    <div className="face-detection-overlay" aria-hidden="true">
      <svg className="face-detection-svg" viewBox="0 0 400 300" preserveAspectRatio="none">
        <rect
          className="face-detection-box"
          x={faceBox.x}
          y={faceBox.y}
          width={faceBox.width}
          height={faceBox.height}
        />

        {FACE_LANDMARK_GROUPS.map(group => (
          <g key={group.id}>
            <polyline
              points={`${group.points.map(toPoint).join(' ')}${group.closed ? ` ${toPoint(group.points[0])}` : ''}`}
              fill="none"
              stroke={group.color}
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {group.points.map(([x, y], index) => (
              <g key={`${group.id}-${index}`}>
                <circle cx={centerX + x} cy={centerY + y} r="1.4" fill="#ffffff" stroke={group.color} strokeWidth="0.8" />
                <text x={centerX + x + 2.6} y={centerY + y - 2.2} className="face-landmark-index">
                  {index}
                </text>
              </g>
            ))}
          </g>
        ))}
      </svg>

      <div className="face-detection-legend">
        {FACE_LANDMARK_GROUPS.map(group => (
          <div className="face-detection-legend-row" key={group.id}>
            <span style={{ background: group.color }} />
            <b>{group.label}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

// High-fidelity face expression illustration
function ExpressionIllustration({ expression }) {
  const lineIndigo = "#818cf8";
  const lineCyan = "#38bdf8";
  const lineRose = "#fb7185";

  const faceContour = "M 100 35 C 65 35, 60 48, 60 98 C 60 148, 72 173, 100 173 C 128 173, 140 148, 140 98 C 140 48, 135 35, 100 35 Z";
  const neckShoulders = "M 82 170 L 82 198 C 82 203, 52 208, 37 220 L 163 220 C 148 208, 118 203, 118 198 L 118 170 Z";

  const nose = <path d="M 98 112 L 100 126 L 104 124" stroke={lineIndigo} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />;

  let eyes, eyebrows, mouth, meshLines;

  switch (expression) {
    case 'at-rest':
      eyes = (
        <>
          <circle cx="85" cy="98" r="3.5" fill={lineCyan} filter="url(#glow-subtle)" />
          <circle cx="115" cy="98" r="3.5" fill={lineCyan} filter="url(#glow-subtle)" />
        </>
      );
      eyebrows = (
        <>
          <path d="M 75 87 Q 85 83 95 87" stroke={lineIndigo} strokeWidth="2.5" fill="none" strokeLinecap="round" />
          <path d="M 105 87 Q 115 83 125 87" stroke={lineIndigo} strokeWidth="2.5" fill="none" strokeLinecap="round" />
        </>
      );
      mouth = (
        <path d="M 82 146 Q 100 148 118 146" stroke={lineRose} strokeWidth="4" fill="none" strokeLinecap="round" filter="url(#glow-subtle)" />
      );
      break;

    case 'pursed-kiss':
      eyes = (
        <>
          <path d="M 76 98 Q 85 103 94 98" stroke={lineCyan} strokeWidth="3" fill="none" strokeLinecap="round" />
          <path d="M 106 98 Q 115 103 124 98" stroke={lineCyan} strokeWidth="3" fill="none" strokeLinecap="round" />
        </>
      );
      eyebrows = (
        <>
          <path d="M 75 86 Q 85 81 95 86" stroke={lineIndigo} strokeWidth="2.5" fill="none" strokeLinecap="round" />
          <path d="M 105 86 Q 115 81 125 86" stroke={lineIndigo} strokeWidth="2.5" fill="none" strokeLinecap="round" />
        </>
      );
      mouth = (
        <circle cx="100" cy="146" r="8" stroke={lineRose} strokeWidth="4" fill="none" filter="url(#glow-subtle)" />
      );
      break;

    case 'closed-smile':
      eyes = (
        <>
          <path d="M 76 98 Q 85 92 94 98" stroke={lineCyan} strokeWidth="3" fill="none" strokeLinecap="round" />
          <path d="M 106 98 Q 115 92 124 98" stroke={lineCyan} strokeWidth="3" fill="none" strokeLinecap="round" />
        </>
      );
      eyebrows = (
        <>
          <path d="M 75 84 Q 85 77 95 84" stroke={lineIndigo} strokeWidth="2.5" fill="none" strokeLinecap="round" />
          <path d="M 105 84 Q 115 77 125 84" stroke={lineIndigo} strokeWidth="2.5" fill="none" strokeLinecap="round" />
        </>
      );
      mouth = (
        <path d="M 78 143 Q 100 158 122 143" stroke={lineRose} strokeWidth="4" fill="none" strokeLinecap="round" filter="url(#glow-subtle)" />
      );
      break;

    case 'grimace':
      eyes = (
        <>
          <circle cx="85" cy="98" r="5.5" fill="none" stroke={lineCyan} strokeWidth="2" filter="url(#glow-subtle)" />
          <circle cx="85" cy="98" r="2.5" fill={lineCyan} />
          <circle cx="115" cy="98" r="5.5" fill="none" stroke={lineCyan} strokeWidth="2" filter="url(#glow-subtle)" />
          <circle cx="115" cy="98" r="2.5" fill={lineCyan} />
        </>
      );
      eyebrows = (
        <>
          <path d="M 75 88 L 93 88" stroke={lineIndigo} strokeWidth="2.5" fill="none" strokeLinecap="round" />
          <path d="M 107 88 L 125 88" stroke={lineIndigo} strokeWidth="2.5" fill="none" strokeLinecap="round" />
        </>
      );
      mouth = (
        <g filter="url(#glow-subtle)">
          <rect x="76" y="140" width="48" height="12" rx="4" fill="rgba(251, 113, 133, 0.15)" stroke={lineRose} strokeWidth="2.5" />
          <line x1="88" y1="140" x2="88" y2="152" stroke={lineRose} strokeWidth="1.5" />
          <line x1="100" y1="140" x2="100" y2="152" stroke={lineRose} strokeWidth="1.5" />
          <line x1="112" y1="140" x2="112" y2="152" stroke={lineRose} strokeWidth="1.5" />
          <line x1="76" y1="146" x2="124" y2="146" stroke={lineRose} strokeWidth="1.5" />
        </g>
      );
      break;

    case 'big-smile':
      eyes = (
        <>
          <circle cx="85" cy="98" r="3.5" fill={lineCyan} filter="url(#glow-subtle)" />
          <circle cx="115" cy="98" r="3.5" fill={lineCyan} filter="url(#glow-subtle)" />
        </>
      );
      eyebrows = (
        <>
          <path d="M 74 83 Q 85 76 95 83" stroke={lineIndigo} strokeWidth="2.5" fill="none" strokeLinecap="round" />
          <path d="M 105 83 Q 115 76 125 83" stroke={lineIndigo} strokeWidth="2.5" fill="none" strokeLinecap="round" />
        </>
      );
      mouth = (
        <g filter="url(#glow-subtle)">
          <path d="M 76 141 C 76 141, 100 135, 124 141 C 124 158, 114 163, 100 163 C 86 163, 76 158, 76 141 Z" fill="rgba(251, 113, 133, 0.25)" stroke={lineRose} strokeWidth="3" />
          <path d="M 80 143 Q 100 141 120 143" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" />
        </g>
      );
      break;

    case 'furrow':
      eyes = (
        <>
          <circle cx="85" cy="99" r="3" fill={lineCyan} filter="url(#glow-subtle)" />
          <circle cx="115" cy="99" r="3" fill={lineCyan} filter="url(#glow-subtle)" />
        </>
      );
      eyebrows = (
        <>
          <path d="M 76 89 Q 86 89 95 93" stroke={lineIndigo} strokeWidth="2.5" fill="none" strokeLinecap="round" />
          <path d="M 105 93 Q 114 89 124 89" stroke={lineIndigo} strokeWidth="2.5" fill="none" strokeLinecap="round" />
        </>
      );
      mouth = (
        <path d="M 82 150 Q 100 143 118 150" stroke={lineRose} strokeWidth="4" fill="none" strokeLinecap="round" filter="url(#glow-subtle)" />
      );
      break;

    default:
      break;
  }

  // Common high-tech scanning overlays
  meshLines = (
    <g opacity="0.45">
      {/* Center axis lines */}
      <line x1="100" y1="20" x2="100" y2="185" stroke="rgba(99, 102, 241, 0.25)" strokeDasharray="3 3" />
      <line x1="45" y1="98" x2="155" y2="98" stroke="rgba(56, 189, 248, 0.25)" strokeDasharray="3 3" />

      {/* Biometric coordinate markers */}
      <path d="M 68 116 L 72 116 L 72 120" stroke="rgba(99, 102, 241, 0.5)" strokeWidth="1" fill="none" />
      <path d="M 132 116 L 128 116 L 128 120" stroke="rgba(99, 102, 241, 0.5)" strokeWidth="1" fill="none" />

      {/* Subtle outer scanning circles */}
      <circle cx="100" cy="98" r="75" stroke="rgba(99, 102, 241, 0.08)" strokeWidth="1" fill="none" strokeDasharray="5 5" />
      <circle cx="100" cy="98" r="85" stroke="rgba(56, 189, 248, 0.05)" strokeWidth="1" fill="none" />

      {/* Eye target indicators */}
      <circle cx="85" cy="98" r="7.5" stroke="rgba(56, 189, 248, 0.3)" strokeWidth="0.75" fill="none" />
      <circle cx="115" cy="98" r="7.5" stroke="rgba(56, 189, 248, 0.3)" strokeWidth="0.75" fill="none" />
    </g>
  );

  return (
    <svg viewBox="0 0 200 220" width="100%" height="100%" style={{ background: '#070a13' }}>
      <defs>
        {/* Soft glowing background element */}
        <radialGradient id="bg-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(99, 102, 241, 0.25)" />
          <stop offset="100%" stopColor="rgba(7, 10, 19, 0)" />
        </radialGradient>

        {/* Semi-transparent futuristic glass gradient */}
        <linearGradient id="face-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="rgba(129, 140, 248, 0.16)" />
          <stop offset="100%" stopColor="rgba(99, 102, 241, 0.03)" />
        </linearGradient>

        {/* Heavy glow for outer frame elements */}
        <filter id="glow-heavy" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="3.5" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>

        {/* Subtle glow for finer vector paths */}
        <filter id="glow-subtle" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.5" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
      </defs>

      {/* Glowing backdrop circular gradient */}
      <rect x="0" y="0" width="200" height="220" fill="url(#bg-glow)" />

      {/* Neck & Shoulders */}
      <path d={neckShoulders} fill="url(#face-gradient)" stroke="rgba(99, 102, 241, 0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

      {/* Left Ear */}
      <path d="M 60 85 C 50 85, 50 115, 60 115" stroke="rgba(99, 102, 241, 0.4)" strokeWidth="1.5" fill="url(#face-gradient)" />
      {/* Right Ear */}
      <path d="M 140 85 C 150 85, 150 115, 140 115" stroke="rgba(99, 102, 241, 0.4)" strokeWidth="1.5" fill="url(#face-gradient)" />

      {/* Face Contour (Glows!) */}
      <path d={faceContour} fill="url(#face-gradient)" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" filter="url(#glow-heavy)" />

      {/* Biometric wireframe grid overlay */}
      {meshLines}

      {/* Interactive expression states */}
      {eyes}
      {eyebrows}
      {nose}
      {mouth}
    </svg>
  );
}

// Hover-to-play looping video thumbnail
function VideoThumbnail({ videoUrl }) {
  const videoRef = useRef(null);

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => { });
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      className="video-thumb-wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <video
        ref={videoRef}
        src={videoUrl}
        loop
        muted
        playsInline
        className="thumb-video"
      />
      <div className="play-overlay-icon">▶</div>
    </div>
  );
}

// ── Face Pose Definitions ──────────────────────────────────────────────────
const FACE_POSES = [
  { id: 'front', label: 'Front', icon: '⬜' },
  { id: '45-left', label: '45° Left', icon: '◧' },
  { id: '45-right', label: '45° Right', icon: '◨' },
  { id: 'left', label: 'Left', icon: '◁' },
  { id: 'right', label: 'Right', icon: '▷' },
  { id: '90-left', label: '90° Left', icon: '◀' },
  { id: '90-right', label: '90° Right', icon: '▶' },
];

// SVG face angle guide overlay for each pose
// Draws a realistic human face silhouette the patient aligns to
// SVG face angle guide overlay for each pose
// Draws a modern, professional camera HUD and alignment guide
function FaceAngleGuide({ pose, showGrid }) {
  const HUD_GREEN = "#10b981";
  const HUD_WHITE = "rgba(255, 255, 255, 0.6)";
  const HUD_BLUE = "#3b82f6";
  const GRID_GRAY = "rgba(255, 255, 255, 0.15)";
  const STROKE_W = 1.5;

  const gridElements = showGrid && (
    <g>
      <line x1="100" y1="0" x2="100" y2="300" stroke={GRID_GRAY} strokeWidth="1" strokeDasharray="5 5" />
      <line x1="300" y1="0" x2="300" y2="300" stroke={GRID_GRAY} strokeWidth="1" strokeDasharray="5 5" />
      <line x1="0" y1="100" x2="400" y2="100" stroke={GRID_GRAY} strokeWidth="1" strokeDasharray="5 5" />
      <line x1="0" y1="200" x2="400" y2="200" stroke={GRID_GRAY} strokeWidth="1" strokeDasharray="5 5" />

      <line x1="200" y1="0" x2="200" y2="300" stroke={HUD_BLUE} strokeWidth="1.5" strokeDasharray="3 4" />
      <line x1="0" y1="140" x2="400" y2="140" stroke={HUD_BLUE} strokeWidth="1.5" strokeDasharray="3 4" />
    </g>
  );

  const PatientHeadLock = ({ x, y }) => (
    <g transform={`translate(${x}, ${y})`}>
      <rect x="-65" y="-12" width="130" height="24" rx="12" fill="#0f172a" fillOpacity="0.85" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
      <circle cx="-50" cy="0" r="3.5" fill={HUD_GREEN} />
      <text x="-40" y="3.5" fill={HUD_GREEN} fontSize="9" fontFamily="system-ui, sans-serif" fontWeight="700" letterSpacing="0.5">PATIENT HEAD LOCK</text>
    </g>
  );

  const BracketCurves = ({ topY, bottomY, cx, width }) => (
    <g stroke={HUD_GREEN} strokeWidth="2" fill="none" strokeDasharray="6 6" strokeLinecap="round">
      <path d={`M ${cx - width / 2} ${topY + 12} Q ${cx} ${topY - 5} ${cx + width / 2} ${topY + 12}`} />
      <path d={`M ${cx - width / 2} ${bottomY - 12} Q ${cx} ${bottomY + 5} ${cx + width / 2} ${bottomY - 12}`} />
    </g>
  );

  const CenterCross = () => (
    <path d="M 193 140 L 207 140 M 200 133 L 200 147" stroke={HUD_GREEN} strokeWidth="2.5" strokeLinecap="round" />
  );

  const EyeLineFront = ({ cy }) => (
    <g stroke={HUD_GREEN} strokeWidth="2" strokeLinecap="round">
      <line x1="120" y1={cy} x2="280" y2={cy} opacity="0.6" />
      <circle cx="160" cy={cy} r="6" fill="none" />
      <circle cx="240" cy={cy} r="6" fill="none" />
    </g>
  );

  const EyeLineProfile = ({ cx, cy, dir }) => {
    const lineX2 = dir > 0 ? 80 : 320;
    return (
      <g stroke={HUD_GREEN} strokeWidth="2" strokeLinecap="round">
        <line x1={cx - 15 * dir} y1={cy} x2={lineX2} y2={cy} opacity="0.6" />
        <circle cx={cx} cy={cy} r="6" fill="none" />
        <line x1={cx} y1={cy - 10} x2={cx} y2={cy + 10} />
      </g>
    );
  };

  // NEW: Perfectly mathematical and smooth curves for generic head shapes
  const FRONT_HEAD = "M 200 30 C 260 30 280 80 280 140 C 280 210 240 250 200 250 C 160 250 120 210 120 140 C 120 80 140 30 200 30 Z";
  const FRONT_SHOULDERS = "M 150 240 C 120 260 80 280 50 300 M 250 240 C 280 260 320 280 350 300";

  const LEFT_PROFILE = "M 200 30 C 260 30 290 80 290 140 C 290 200 260 230 200 250 C 180 250 155 220 145 205 C 135 190 145 185 150 175 C 150 170 145 165 140 165 C 145 155 150 155 150 155 L 125 140 C 140 120 150 115 145 100 C 140 85 160 40 200 30 Z";
  const LEFT_SHOULDERS = "M 200 250 C 240 260 280 280 300 300 M 170 235 C 150 260 100 280 50 300";

  const RIGHT_PROFILE = "M 200 30 C 140 30 110 80 110 140 C 110 200 140 230 200 250 C 220 250 245 220 255 205 C 265 190 255 185 250 175 C 250 170 255 165 260 165 C 255 155 250 155 250 155 L 275 140 C 260 120 250 115 255 100 C 260 85 240 40 200 30 Z";
  const RIGHT_SHOULDERS = "M 200 250 C 160 260 120 280 100 300 M 230 235 C 250 260 300 280 350 300";

  let silhouette = null;

  if (pose === 'front') {
    silhouette = (
      <>
        <path d={FRONT_HEAD} fill="none" stroke={HUD_WHITE} strokeWidth={STROKE_W} strokeDasharray="6 8" />
        <path d={FRONT_SHOULDERS} fill="none" stroke={HUD_WHITE} strokeWidth={STROKE_W} strokeDasharray="4 8" opacity="0.5" />

        <BracketCurves topY={30} bottomY={250} cx={200} width={100} />
        <EyeLineFront cy={125} />
        <CenterCross />
        {/* <PatientHeadLock x={200} y={185} /> */}
      </>
    );
  } else if (pose === '45-left') {
    silhouette = (
      <>
        {/* Slightly narrower oval for 45 deg */}
        <ellipse cx="200" cy="140" rx="70" ry="110" fill="none" stroke={HUD_WHITE} strokeWidth={STROKE_W} strokeDasharray="6 8" />
        <path d={FRONT_SHOULDERS} fill="none" stroke={HUD_WHITE} strokeWidth={STROKE_W} strokeDasharray="4 8" opacity="0.5" />

        <BracketCurves topY={30} bottomY={250} cx={200} width={90} />
        <EyeLineFront cy={125} />
        <CenterCross />
        {/* <PatientHeadLock x={200} y={185} /> */}
      </>
    );
  } else if (pose === '45-right') {
    silhouette = (
      <>
        <ellipse cx="200" cy="140" rx="70" ry="110" fill="none" stroke={HUD_WHITE} strokeWidth={STROKE_W} strokeDasharray="6 8" />
        <path d={FRONT_SHOULDERS} fill="none" stroke={HUD_WHITE} strokeWidth={STROKE_W} strokeDasharray="4 8" opacity="0.5" />

        <BracketCurves topY={30} bottomY={250} cx={200} width={90} />
        <EyeLineFront cy={125} />
        <CenterCross />
        {/* <PatientHeadLock x={200} y={185} /> */}
      </>
    );
  } else if (pose === 'left' || pose === '90-left') {
    silhouette = (
      <>
        <path d={LEFT_PROFILE} fill="none" stroke={HUD_WHITE} strokeWidth={STROKE_W} strokeDasharray="6 8" strokeLinecap="round" strokeLinejoin="round" />
        <path d={LEFT_SHOULDERS} fill="none" stroke={HUD_WHITE} strokeWidth={STROKE_W} strokeDasharray="4 8" opacity="0.5" />

        <BracketCurves topY={30} bottomY={250} cx={200} width={100} />
        <EyeLineProfile cx={155} cy={125} dir={-1} />
        <CenterCross />
        {/* <PatientHeadLock x={200} y={185} /> */}
      </>
    );
  } else if (pose === 'right' || pose === '90-right') {
    silhouette = (
      <>
        <path d={RIGHT_PROFILE} fill="none" stroke={HUD_WHITE} strokeWidth={STROKE_W} strokeDasharray="6 8" strokeLinecap="round" strokeLinejoin="round" />
        <path d={RIGHT_SHOULDERS} fill="none" stroke={HUD_WHITE} strokeWidth={STROKE_W} strokeDasharray="4 8" opacity="0.5" />

        <BracketCurves topY={30} bottomY={250} cx={200} width={100} />
        <EyeLineProfile cx={245} cy={125} dir={1} />
        <CenterCross />
        {/* <PatientHeadLock x={200} y={185} /> */}
      </>
    );
  }

  return (
    <svg className="silhouette-svg" viewBox="0 0 400 300" xmlns="http://www.w3.org/2000/svg">
      {gridElements}
      {silhouette}
      <text x="200" y="290" fill="rgba(255,255,255,0.8)" textAnchor="middle" fontSize="11" fontFamily="system-ui, sans-serif" fontWeight="500" letterSpacing="1">
        ALIGN PATIENT WITHIN OUTLINE
      </text>
    </svg>
  );
}


// Grayscale brightness-normalized absolute difference check (Mean-Subtracted MAE)
function calculateNormalizedSimilarity(data1, data2, centroid1, centroid2, pose, isMirrored) {
  if (!data1 || !data2 || data1.length !== data2.length) return 0;

  // Fallback to center if centroids are not provided or invalid
  const cx1 = centroid1 ? Math.round(centroid1.x / 10) : 20;
  const cy1 = centroid1 ? Math.round(centroid1.y / 10) : 15;
  const cx2 = centroid2 ? Math.round(centroid2.x / 10) : 20;
  const cy2 = centroid2 ? Math.round(centroid2.y / 10) : 15;

  let sum1 = 0;
  let sum2 = 0;
  let count = 0;

  const l1Array = [];
  const l2Array = [];

  // Iterate over a 20x20 window centered on each centroid
  const halfW = 10;
  const halfH = 10;

  for (let dy = -halfH; dy <= halfH; dy++) {
    for (let dx = -halfW; dx <= halfW; dx++) {
      const x1 = cx1 + dx;
      const y1 = cy1 + dy;
      const x2 = cx2 + dx;
      const y2 = cy2 + dy;

      // Ensure both points are within the 40x30 bounds
      if (x1 >= 0 && x1 < 40 && y1 >= 0 && y1 < 30 &&
        x2 >= 0 && x2 < 40 && y2 >= 0 && y2 < 30) {

        // If the live frame data (data1) is not mirrored, but the baseline is mirrored,
        // we flip the live frame x-coordinate to align them correctly.
        const rx1 = isMirrored ? (39 - x1) : x1;

        const idx1 = (y1 * 40 + rx1) * 4;
        const idx2 = (y2 * 40 + x2) * 4;

        const l1 = 0.299 * data1[idx1] + 0.587 * data1[idx1 + 1] + 0.114 * data1[idx1 + 2];
        const l2 = 0.299 * data2[idx2] + 0.587 * data2[idx2 + 1] + 0.114 * data2[idx2 + 2];

        l1Array.push(l1);
        l2Array.push(l2);

        sum1 += l1;
        sum2 += l2;
        count++;
      }
    }
  }

  if (count === 0) return 0;

  const mean1 = sum1 / count;
  const mean2 = sum2 / count;

  let absoluteDiff = 0;
  for (let i = 0; i < count; i++) {
    const n1 = l1Array[i] - mean1;
    const n2 = l2Array[i] - mean2;
    absoluteDiff += Math.abs(n1 - n2);
  }

  const avgDiff = absoluteDiff / count;

  // Set parameters dynamically based on the active face angle
  let minDiff = 12; // default offset
  let range = 40;   // default range (divisor)

  if (pose === 'front') {
    // Front view: very clear and straight. Stricter thresholds.
    minDiff = 10;
    range = 35;
  } else if (pose === '45-left' || pose === '45-right') {
    // 45 degree views: partial rotation. Moderate threshold.
    minDiff = 12;
    range = 45;
  } else if (pose === 'left' || pose === 'right') {
    // Side views: moderate-to-high rotation.
    minDiff = 14;
    range = 50;
  } else if (pose === '90-left' || pose === '90-right') {
    // 90 degree views: profile view. Stricter to block straight-face matches.
    minDiff = 10;
    range = 28;
  }

  const score = Math.max(0, 1 - Math.max(0, avgDiff - minDiff) / range) * 100;
  return score;
}

// Parse URL query parameters helper
const getURLParams = () => {
  if (typeof window === 'undefined') return { imgUrl: null, faceX: 200, faceY: 150, hasCustomFacePos: false, facePos: 'front' };
  const params = new URLSearchParams(window.location.search);
  let imgUrl = params.get('img_url') || params.get('imgUrl');
  if (imgUrl && imgUrl.startsWith('data:') && imgUrl.includes(' ')) {
    imgUrl = imgUrl.replace(/ /g, '+');
  }
  const fxStr = params.get('face_x') || params.get('faceX') || params.get('x');
  const fyStr = params.get('face_y') || params.get('faceY') || params.get('y');
  const faceX = fxStr !== null && !isNaN(parseFloat(fxStr)) ? parseFloat(fxStr) : 200;
  const faceY = fyStr !== null && !isNaN(parseFloat(fyStr)) ? parseFloat(fyStr) : 150;

  const facePosParam = params.get('face_pos') || params.get('facePos');
  const rawFacePos = facePosParam ? facePosParam.replace(/['"]/g, '') : null;
  const VALID_POSES = FACE_POSES.map(p => p.id);
  const facePos = (rawFacePos && VALID_POSES.includes(rawFacePos)) ? rawFacePos : 'front';

  return {
    imgUrl: imgUrl && imgUrl !== 'null' ? imgUrl : null,
    faceX,
    faceY,
    hasCustomFacePos: fxStr !== null && fyStr !== null,
    facePos
  };
};

export default function GhostCamera({ defaultTab = 'photos' }) {
  const { imgUrl, faceX, faceY, hasCustomFacePos, facePos } = getURLParams();

  const [theme, setTheme] = useState('light'); // 'light', 'dark'
  const [stage, setStage] = useState(imgUrl ? 'capture-after' : 'capture-before'); // 'capture-before', 'capture-after', 'review'
  const [pose, setPose] = useState(facePos); // 'front', '45-left', '45-right', 'left', 'right', '90-left', '90-right'
  const [beforePhotos, setBeforePhotos] = useState({
    'front': facePos === 'front' ? imgUrl : null,
    '45-left': facePos === '45-left' ? imgUrl : null,
    '45-right': facePos === '45-right' ? imgUrl : null,
    'left': facePos === 'left' ? imgUrl : null,
    'right': facePos === 'right' ? imgUrl : null,
    '90-left': facePos === '90-left' ? imgUrl : null,
    '90-right': facePos === '90-right' ? imgUrl : null
  });
  const [afterPhotos, setAfterPhotos] = useState({
    'front': null,
    '45-left': null,
    '45-right': null,
    'left': null,
    'right': null,
    '90-left': null,
    '90-right': null
  });
  const [beforeCapturedReview, setBeforeCapturedReview] = useState(false);

  const beforePhoto = beforePhotos[pose] || null;
  const afterPhoto = afterPhotos[pose] || null;

  const setBeforePhoto = (val) => {
    setBeforePhotos(prev => ({
      ...prev,
      [pose]: typeof val === 'function' ? val(prev[pose]) : val
    }));
  };

  const setAfterPhoto = (val) => {
    setAfterPhotos(prev => ({
      ...prev,
      [pose]: typeof val === 'function' ? val(prev[pose]) : val
    }));
  };

  // Video-based states
  const [activeTab, setActiveTab] = useState(defaultTab); // 'photos', 'videos'
  const [recordedVideos, setRecordedVideos] = useState({
    before: {
      'at-rest': null,
      'pursed-kiss': null,
      'closed-smile': null,
      'grimace': null,
      'big-smile': null,
      'furrow': null
    },
    after: {
      'at-rest': null,
      'pursed-kiss': null,
      'closed-smile': null,
      'grimace': null,
      'big-smile': null,
      'furrow': null
    }
  });
  const [videoPhase, setVideoPhase] = useState('before'); // 'before', 'after'
  const [activeVideoExpression, setActiveVideoExpression] = useState(null);
  const [videoMode, setVideoMode] = useState('grid'); // 'grid', 'recording', 'preview', 'playback', 'compare'
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [tempVideoUrl, setTempVideoUrl] = useState(null);

  const mediaRecorderRef = useRef(null);
  const recordedBlobsRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const createdUrlsRef = useRef([]);
  const compareBeforeRef = useRef(null);
  const compareAfterRef = useRef(null);
  const overlayVideoRef = useRef(null);
  const [comparePlaying, setComparePlaying] = useState(false);

  // AI Clinic states
  const [aiBackgroundRemoval, setAiBackgroundRemoval] = useState(true);
  const [liveFaceTracking, setLiveFaceTracking] = useState(!hasCustomFacePos);

  // Camera feed states
  const [cameraDevices, setCameraDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  // Settings/Modifiers
  const [ghostOpacity, setGhostOpacity] = useState(0.5);
  const [mirrorCamera, setMirrorCamera] = useState(false);
  const [showSilhouette, setShowSilhouette] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const [triggerFlash, setTriggerFlash] = useState(false);
  const [autoCaptureEnabled, setAutoCaptureEnabled] = useState(true);
  const [alignmentTime, setAlignmentTime] = useState(0);
  const [isFaceDetected, setIsFaceDetected] = useState(false);

  // Review modes: 'split', 'side-by-side', 'blend'
  const [reviewTab, setReviewTab] = useState('split');
  const [splitOffset, setSplitOffset] = useState(50); // percentage for horizontal split
  const [blendOpacity, setBlendOpacity] = useState(0.5); // opacity for blend review mode

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const splitContainerRef = useRef(null);
  const viewportRef = useRef(null);
  const fileInputRef = useRef(null);
  const afterFileInputRef = useRef(null);
  const genericFileInputRef = useRef(null);
  const [isDraggingSplit, setIsDraggingSplit] = useState(false);

  // Viewport Drag & Scale states for overlay alignment
  const [overlayOffset, setOverlayOffset] = useState({ x: 0, y: 0 });
  const [overlayScale, setOverlayScale] = useState(1);
  const [isDraggingOverlay, setIsDraggingOverlay] = useState(false);
  const [showGhostOverlay, setShowGhostOverlay] = useState(true);
  const [autoAlignShadow, setAutoAlignShadow] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  // Face Detection / Centroid tracking states
  const [facePosition, setFacePosition] = useState({ x: faceX, y: faceY });
  const facePosRef = useRef({ x: faceX, y: faceY });
  const [capturedBeforeFacePositions, setCapturedBeforeFacePositions] = useState({
    'front': (imgUrl && facePos === 'front') ? { x: faceX, y: faceY } : null,
    '45-left': (imgUrl && facePos === '45-left') ? { x: faceX, y: faceY } : null,
    '45-right': (imgUrl && facePos === '45-right') ? { x: faceX, y: faceY } : null,
    'left': (imgUrl && facePos === 'left') ? { x: faceX, y: faceY } : null,
    'right': (imgUrl && facePos === 'right') ? { x: faceX, y: faceY } : null,
    '90-left': (imgUrl && facePos === '90-left') ? { x: faceX, y: faceY } : null,
    '90-right': (imgUrl && facePos === '90-right') ? { x: faceX, y: faceY } : null
  });
  const [capturedAfterFacePositions, setCapturedAfterFacePositions] = useState({
    'front': null,
    '45-left': null,
    '45-right': null,
    'left': null,
    'right': null,
    '90-left': null,
    '90-right': null
  });

  const capturedBeforeFacePos = capturedBeforeFacePositions[pose] || null;
  const capturedAfterFacePos = capturedAfterFacePositions[pose] || null;

  const setCapturedBeforeFacePos = (val) => {
    setCapturedBeforeFacePositions(prev => ({
      ...prev,
      [pose]: typeof val === 'function' ? val(prev[pose]) : val
    }));
  };

  const setCapturedAfterFacePos = (val) => {
    setCapturedAfterFacePositions(prev => ({
      ...prev,
      [pose]: typeof val === 'function' ? val(prev[pose]) : val
    }));
  };

  const handlePoseChange = (newPose) => {
    setPose(newPose);
    if (!beforePhotos[newPose]) {
      setStage('capture-before');
      setBeforeCapturedReview(false);
    } else if (!afterPhotos[newPose]) {
      setStage('capture-after');
      setBeforeCapturedReview(false);
    } else {
      setStage('review');
    }
  };
  const [autoAlignPostCapture, setAutoAlignPostCapture] = useState(true);

  const beforePhotoDataRef = useRef(null);
  const liveFrameDataRef = useRef(null);

  // Extract pixel data of beforePhoto whenever it is set
  useEffect(() => {
    if (!beforePhoto) {
      beforePhotoDataRef.current = null;
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 40;
      canvas.height = 30;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, 40, 30);
      try {
        const imgData = ctx.getImageData(0, 0, 40, 30);
        beforePhotoDataRef.current = imgData.data;
      } catch (e) {
        console.warn("Failed to get pixel data of beforePhoto:", e);
      }
    };
    img.src = beforePhoto;
  }, [beforePhoto]);

  // Module 3: Upload Management states
  const [uploadStatus, setUploadStatus] = useState('idle'); // 'idle', 'uploading', 'facemesh', 'verifying', 'stabilizing', 'preparing', 'completed'
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadFileType, setUploadFileType] = useState('');

  // Module 4: Photo Viewer states (Zoom, Pan, Fullscreen)
  const [photoZoom, setPhotoZoom] = useState(1);
  const [photoPan, setPhotoPan] = useState({ x: 0, y: 0 });
  const [isPanningPhoto, setIsPanningPhoto] = useState(false);
  const photoPanStart = useRef({ x: 0, y: 0 });
  const [isFullscreenPhoto, setIsFullscreenPhoto] = useState(false);

  // Module 5: Video Viewer states (Playback Speed, Frame sync, seek bar)
  const [videoSpeed, setVideoSpeed] = useState(1); // 0.5, 1, 1.5, 2
  const [videoTime, setVideoTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(10); // Ideal duration 10s
  const [isFullscreenVideoCompare, setIsFullscreenVideoCompare] = useState(false);
  const [comparisonMode, setComparisonMode] = useState('photos'); // 'photos', 'videos'
  const [selectedVideoCompareExpression, setSelectedVideoCompareExpression] = useState('at-rest');

  // Module 6 & 7: Verification & Alignment Dashboard states
  const [similarityScore, setSimilarityScore] = useState(98.7); // similarity score

  // Module 8: WoundMatic Capture states
  const [woundCapturedImage, setWoundCapturedImage] = useState(null);
  const [woundProcessing, setWoundProcessing] = useState(false);
  const [woundMetrics, setWoundMetrics] = useState(null); // { length: 4.5, width: 3.1, area: 13.95 }
  const [woundCameraDevices, setWoundCameraDevices] = useState([]);
  const [woundSelectedDeviceId, setWoundSelectedDeviceId] = useState('');
  const [woundCameraLoading, setWoundCameraLoading] = useState(false);
  const [woundCameraError, setWoundCameraError] = useState(null);
  const woundVideoRef = useRef(null);
  const woundCanvasRef = useRef(null);

  // EMR API Simulator Logs state
  const [apiLogs, setApiLogs] = useState([
    { timestamp: new Date().toLocaleTimeString(), method: 'GET', url: `/api/patient/884-9020-MED`, status: 200, payload: { id: '884-9020-MED', firstName: 'Sarah', lastName: 'Jenkins', dob: '10/14/1990' } }
  ]);
  const [isApiConsoleExpanded, setIsApiConsoleExpanded] = useState(false);

  const logApiCall = (method, url, status, payload = null) => {
    setApiLogs(prev => [
      {
        timestamp: new Date().toLocaleTimeString(),
        method,
        url,
        status,
        payload
      },
      ...prev.slice(0, 49) // Keep last 50 logs
    ]);
  };

  const simulateUploadWorkflow = (file, callbackOnComplete) => {
    if (!file) return;
    setUploadFileName(file.name);
    setUploadFileType(file.type);
    setUploadStatus('uploading');
    setUploadProgress(0);
    logApiCall('POST', '/api/media/upload', 202, { filename: file.name, type: file.type });

    // Step-by-step progress simulation
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setUploadProgress(progress);
      if (progress >= 100) {
        clearInterval(interval);

        // Move to face mesh
        setUploadStatus('facemesh');
        logApiCall('GET', '/api/status/job-facemesh-01', 200, { status: 'Processing Face Mesh...' });

        setTimeout(() => {
          setUploadStatus('verifying');
          logApiCall('GET', '/api/status/job-verify-01', 200, { status: 'Verifying Patient...' });

          setTimeout(() => {
            const isVideo = file.type.startsWith('video/') || file.name.endsWith('.mp4') || file.name.endsWith('.mov');
            if (isVideo) {
              setUploadStatus('stabilizing');
              logApiCall('GET', '/api/status/job-stabilize-01', 200, { status: 'Stabilizing Video...' });
            } else {
              setUploadStatus('stabilizing'); // Aligning features
              logApiCall('GET', '/api/status/job-align-01', 200, { status: 'Aligning Features...' });
            }

            setTimeout(() => {
              setUploadStatus('preparing');
              logApiCall('GET', '/api/status/job-compare-01', 200, { status: 'Preparing Comparison...' });

              setTimeout(() => {
                setUploadStatus('completed');
                logApiCall('GET', '/api/status/job-complete-01', 200, { status: 'Completed' });
                logApiCall('GET', `/api/alignment/comp-photo-01`, 200, { headRotation: 0.98, eyePosition: 0.99, scaleMatch: 0.97, lightingMatch: 0.92 });

                setTimeout(() => {
                  setUploadStatus('idle');
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    callbackOnComplete(event.target.result);
                  };
                  reader.readAsDataURL(file);
                }, 500);

              }, 1000);
            }, 1000);
          }, 1000);
        }, 1000);
      }
    }, 80);
  };

  // File Upload Handlers for custom Before photo
  const handleBeforeUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    simulateUploadWorkflow(file, (dataUrl) => {
      setBeforePhoto(dataUrl);
      setStage('capture-after'); // Transition immediately to After capture mode!
    });
  };

  const handleAfterUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    simulateUploadWorkflow(file, (dataUrl) => {
      setAfterPhoto(dataUrl);
      setStage('review'); // Transition to review!
    });
  };

  const triggerBeforeUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const triggerAfterUpload = () => {
    if (afterFileInputRef.current) {
      afterFileInputRef.current.click();
    }
  };

  // Switch to overlay mode to align camera to the captured Before photo
  const handleUseAsGhostGuide = () => {
    if (beforePhoto) {
      setStage('capture-after');
    }
  };

  const handleOverlayPointerDown = (e) => {
    const isPhotoAfter = activeTab === 'photos' && stage === 'capture-after';
    const isVideoAfter = activeTab === 'videos' && videoPhase === 'after' && videoMode === 'recording';
    if (!isPhotoAfter && !isVideoAfter) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDraggingOverlay(true);
    dragStart.current = { x: e.clientX - overlayOffset.x, y: e.clientY - overlayOffset.y };
  };

  const handleOverlayPointerMove = (e) => {
    if (!isDraggingOverlay) return;
    const newX = e.clientX - dragStart.current.x;
    const newY = e.clientY - dragStart.current.y;
    setOverlayOffset({ x: newX, y: newY });
  };

  const handleOverlayPointerUp = (e) => {
    setIsDraggingOverlay(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const handleResetOverlay = () => {
    setOverlayOffset({ x: 0, y: 0 });
    setOverlayScale(1);
    setAutoAlignShadow(false);
  };

  const handleSnapToFace = () => {
    if (!capturedBeforeFacePos || !viewportRef.current) return;
    const rect = viewportRef.current.getBoundingClientRect();
    const dx = ((facePosRef.current.x - capturedBeforeFacePos.x) / 400) * rect.width;
    const dy = ((facePosRef.current.y - capturedBeforeFacePos.y) / 300) * rect.height;
    setOverlayOffset({ x: dx, y: dy });
  };

  // Real-time auto-alignment effect to follow user's face position
  useEffect(() => {
    const isPhotoAfter = activeTab === 'photos' && stage === 'capture-after';
    const isVideoAfter = activeTab === 'videos' && videoPhase === 'after' && videoMode === 'recording';
    if ((isPhotoAfter || isVideoAfter) && autoAlignShadow && capturedBeforeFacePos && viewportRef.current) {
      const rect = viewportRef.current.getBoundingClientRect();
      const dx = ((facePosition.x - capturedBeforeFacePos.x) / 400) * rect.width;
      const dy = ((facePosition.y - capturedBeforeFacePos.y) / 300) * rect.height;
      setOverlayOffset({ x: dx, y: dy });
    }
  }, [facePosition, autoAlignShadow, stage, activeTab, videoPhase, videoMode, capturedBeforeFacePos]);

  // Store latest handleCapture in a ref so useEffect can invoke it without triggering re-runs
  const handleCaptureRef = useRef(null);
  useEffect(() => {
    handleCaptureRef.current = handleCapture;
  });

  const alignmentStartTimeRef = useRef(null);

  // Automatically capture the photo once alignment is held for 1.0 second (stable hold)
  useEffect(() => {
    if (!autoCaptureEnabled || stage !== 'capture-after' || !isFaceDetected) {
      alignmentStartTimeRef.current = null;
      setAlignmentTime(0);
      return;
    }

    const target = capturedBeforeFacePos || { x: faceX, y: faceY };

    const dist = Math.sqrt(
      Math.pow(facePosition.x - target.x, 2) +
      Math.pow(facePosition.y - target.y, 2)
    );

    // Calculate match percentage based on centroid overlap and pose similarity
    const centroidScore = Math.max(0, 1 - dist / 70) * 100;

    let poseScore = 100;
    if (beforePhotoDataRef.current && liveFrameDataRef.current) {
      poseScore = calculateNormalizedSimilarity(
        liveFrameDataRef.current,
        beforePhotoDataRef.current,
        facePosition,
        capturedBeforeFacePos,
        pose,
        mirrorCamera
      );
    }

    const matchPercent = Math.round(centroidScore * 0.4 + poseScore * 0.6);
    const isAligned = matchPercent >= 90; // 90% alignment is required

    if (isAligned) {
      if (alignmentStartTimeRef.current === null) {
        alignmentStartTimeRef.current = Date.now();
        setAlignmentTime(0);
      } else {
        const elapsed = Date.now() - alignmentStartTimeRef.current;
        setAlignmentTime(Math.min(1000, elapsed));
        if (elapsed >= 1000) {
          alignmentStartTimeRef.current = null;
          setAlignmentTime(0);
          if (handleCaptureRef.current) {
            handleCaptureRef.current();
          }
        }
      }
    } else {
      alignmentStartTimeRef.current = null;
      setAlignmentTime(0);
    }
  }, [facePosition, stage, capturedBeforeFacePos, autoCaptureEnabled, isFaceDetected, pose, mirrorCamera]);

  // Detect and enumerate available video input sources
  useEffect(() => {
    async function enumerate() {
      try {
        setCameraLoading(true);
        // Prompt camera permission initially
        const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
        // Close tracks immediately to avoid keeping stream locked during query
        tempStream.getTracks().forEach(track => track.stop());

        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputs = devices.filter(d => d.kind === 'videoinput');
        setCameraDevices(videoInputs);
        if (videoInputs.length > 0) {
          setSelectedDeviceId(videoInputs[0].deviceId);
        }
        setCameraLoading(false);
      } catch (err) {
        console.error("Camera access or enumeration failed:", err);
        setCameraError("Camera access denied or no devices found. Please enable permission or use the Demo mode.");
        setCameraLoading(false);
      }
    }
    enumerate();
  }, []);

  // Control camera stream based on selectedDeviceId, stage, activeTab, and videoMode
  useEffect(() => {
    const shouldRunCamera = activeTab === 'photos'
      ? (stage === 'capture-before' || stage === 'capture-after')
      : (videoMode === 'recording');

    if (!shouldRunCamera) return;

    let activeStream = null;

    async function startStream() {
      try {
        setCameraLoading(true);
        setCameraError(null);

        let constraints;
        if (selectedDeviceId) {
          constraints = {
            video: {
              deviceId: { exact: selectedDeviceId },
              width: { ideal: 1280 },
              height: { ideal: 960 }
            }
          };
        } else {
          constraints = {
            video: {
              width: { ideal: 1280 },
              height: { ideal: 960 }
            }
          };
        }

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(e => console.warn("Video play failed:", e));
          activeStream = stream;
        }
        setCameraLoading(false);
      } catch (err) {
        console.warn("Could not start camera with exact constraints. Retrying with loose constraints...", err);
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({ video: true });
          if (videoRef.current) {
            videoRef.current.srcObject = fallbackStream;
            videoRef.current.play().catch(e => console.warn("Fallback video play failed:", e));
            activeStream = fallbackStream;
          }
          setCameraLoading(false);
        } catch (fallbackErr) {
          setCameraError("Unable to load video stream: " + fallbackErr.message);
          setCameraLoading(false);
        }
      }
    }

    startStream();

    return () => {
      if (activeStream) {
        activeStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [selectedDeviceId, stage, activeTab, videoMode]);

  // Programmatically play the overlay video to bypass autoplay restrictions on dynamic blob URLs
  useEffect(() => {
    if (videoMode === 'recording' && videoPhase === 'after' && overlayVideoRef.current) {
      overlayVideoRef.current.play().catch(err => {
        console.warn("Failed to play overlay video:", err);
      });
    }
  }, [videoMode, videoPhase, activeVideoExpression, recordedVideos]);

  // Viewport Render Loop for Canvas and Face Centroid Tracking
  useEffect(() => {
    let animationFrameId;

    const render = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas) {
        animationFrameId = requestAnimationFrame(render);
        return;
      }

      const ctx = canvas.getContext('2d');
      if (video.readyState >= 2) {
        if (canvas.width !== video.videoWidth) {
          canvas.width = video.videoWidth || 640;
          canvas.height = video.videoHeight || 480;
        }

        // 1. Skin Color Face tracking centroid detection
        if (liveFaceTracking) {
          const tempCanvas = document.createElement('canvas');
          const tempCtx = tempCanvas.getContext('2d');
          tempCanvas.width = 40;
          tempCanvas.height = 30;
          const w = video.videoWidth || 640;
          const h = video.videoHeight || 480;
          const croppedW = w / ZOOM_FACTOR;
          const croppedH = h / ZOOM_FACTOR;
          const startX = (w - croppedW) / 2;
          const startY = (h - croppedH) / 2;
          tempCtx.drawImage(video, startX, startY, croppedW, croppedH, 0, 0, 40, 30);

          try {
            const imgData = tempCtx.getImageData(0, 0, 40, 30);
            const data = imgData.data;
            let weightedSumX = 0;
            let weightedSumY = 0;
            let totalWeight = 0;

            const currentFacePos = facePosRef.current || { x: faceX, y: faceY };
            const trackX = (mirrorCamera ? (400 - currentFacePos.x) : currentFacePos.x) / 10;
            const trackY = currentFacePos.y / 10;

            for (let y = 0; y < 30; y++) {
              for (let x = 0; x < 40; x++) {
                const idx = (y * 40 + x) * 4;
                const r = data[idx];
                const g = data[idx + 1];
                const b = data[idx + 2];

                // Robust skin color rule (works under warm/cool lighting and all skin tones)
                const max = Math.max(r, g, b);
                const min = Math.min(r, g, b);

                // Exclude neutral beige/tan background walls by checking the red-to-green dominance over green-to-blue.
                // Human skin has a reddish blood-tone tint, meaning R - G is significantly larger than G - B.
                const isSkinColor = (r - g) > (g - b) * 1.1;

                const isSkin = r > 40 && g > 20 && b > 10 &&
                  (max - min > 8) &&
                  (r - g > 8) &&
                  (r > b) &&
                  isSkinColor;

                if (isSkin) {
                  const dx = x - trackX;
                  const dy = y - trackY;
                  const distSq = dx * dx + dy * dy;
                  // Spatial weight prioritizing pixels close to the current tracking center (sigma = 6)
                  const weight = Math.exp(-distSq / 72);
                  weightedSumX += x * weight;
                  weightedSumY += y * weight;
                  totalWeight += weight;
                }
              }
            }

            if (totalWeight > 2.0) {
              liveFrameDataRef.current = data;
              let targetX = (weightedSumX / totalWeight) / 40 * 400;
              if (mirrorCamera) {
                targetX = 400 - targetX;
              }
              const targetY = (weightedSumY / totalWeight) / 30 * 300;

              // Low-pass filter interpolation
              facePosRef.current.x = facePosRef.current.x * 0.88 + targetX * 0.12;
              facePosRef.current.y = facePosRef.current.y * 0.88 + targetY * 0.12;

              // Boundary clamping - keep tracker near the silhouette target region (center) to avoid background drift
              facePosRef.current.x = Math.max(140, Math.min(260, facePosRef.current.x));
              facePosRef.current.y = Math.max(100, Math.min(200, facePosRef.current.y));

              setFacePosition({ x: facePosRef.current.x, y: facePosRef.current.y });
              setIsFaceDetected(true);
            } else {
              setIsFaceDetected(false);
              liveFrameDataRef.current = null;
              // Gently drift search center back to default/URL coordinates
              facePosRef.current.x = facePosRef.current.x * 0.9 + faceX * 0.1;
              facePosRef.current.y = facePosRef.current.y * 0.9 + faceY * 0.1;
            }
          } catch (e) {
            console.error("Skin tracking read error:", e);
            setIsFaceDetected(false);
            liveFrameDataRef.current = null;
          }
        } else {
          facePosRef.current = { x: faceX, y: faceY };
          setFacePosition({ x: faceX, y: faceY });
          setIsFaceDetected(false);
          liveFrameDataRef.current = null;
        }

        // 2. Viewport Painting
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Standard viewport drawing (always show normal camera feed with room background in the viewfinder)
        ctx.save();
        if (mirrorCamera) {
          ctx.translate(canvas.width, 0);
          ctx.scale(-1, 1);
        }
        const w = video.videoWidth || 640;
        const h = video.videoHeight || 480;
        const croppedW = w / ZOOM_FACTOR;
        const croppedH = h / ZOOM_FACTOR;
        const startX = (w - croppedW) / 2;
        const startY = (h - croppedH) / 2;
        ctx.drawImage(video, startX, startY, croppedW, croppedH, 0, 0, canvas.width, canvas.height);
        ctx.restore();
      }
      animationFrameId = requestAnimationFrame(render);
    };

    // Start loop
    render();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [aiBackgroundRemoval, theme, mirrorCamera, liveFaceTracking, stage, activeTab, videoMode]);

  const handleCapture = () => {
    if (!canvasRef.current) return;

    let dataUrl;
    const video = videoRef.current;

    if (video && video.readyState >= video.HAVE_CURRENT_DATA) {
      // 2. Generate portrait photo with room background (always preserve original background color/details on capture)
      const offscreenSolid = document.createElement('canvas');
      offscreenSolid.width = video.videoWidth || 640;
      offscreenSolid.height = video.videoHeight || 480;
      const sCtx = offscreenSolid.getContext('2d');

      sCtx.save();
      if (mirrorCamera) {
        sCtx.translate(offscreenSolid.width, 0);
        sCtx.scale(-1, 1);
      }
      const w = video.videoWidth || 640;
      const h = video.videoHeight || 480;
      const croppedW = w / ZOOM_FACTOR;
      const croppedH = h / ZOOM_FACTOR;
      const startX = (w - croppedW) / 2;
      const startY = (h - croppedH) / 2;
      sCtx.drawImage(video, startX, startY, croppedW, croppedH, 0, 0, offscreenSolid.width, offscreenSolid.height);
      sCtx.restore();
      dataUrl = offscreenSolid.toDataURL('image/png');
    } else {
      // Fallback: capture from visible canvas (which already did rendering)
      dataUrl = canvasRef.current.toDataURL('image/png');
    }

    // Trigger visual camera shutter flash
    setTriggerFlash(true);
    setTimeout(() => setTriggerFlash(false), 400);

    if (stage === 'capture-before') {
      setBeforePhoto(dataUrl);
      setCapturedBeforeFacePos({ ...facePosRef.current });
      setBeforeCapturedReview(true); // Switch to intermediate review mode
    } else if (stage === 'capture-after') {
      setAfterPhoto(dataUrl);
      setCapturedAfterFacePos({ ...facePosRef.current });
      setStage('review'); // Switch to Review mode automatically
    }
  };

  // Skip camera and load mock SVGs directly for demo purposes
  const handleLoadDemo = () => {
    const newBefores = {};
    const newAfters = {};
    const newBeforePositions = {};
    const newAfterPositions = {};

    Object.keys(beforePhotos).forEach(p => {
      newBefores[p] = MOCK_BEFORE;
      newAfters[p] = MOCK_AFTER;
      newBeforePositions[p] = { x: faceX, y: faceY };
      newAfterPositions[p] = { x: faceX + 5, y: faceY + 2 };
    });

    setBeforePhotos(newBefores);
    setAfterPhotos(newAfters);
    setCapturedBeforeFacePositions(newBeforePositions);
    setCapturedAfterFacePositions(newAfterPositions);
    setStage('review');

    // Load high-quality demo video loops
    setRecordedVideos({
      before: {
        'at-rest': 'https://assets.mixkit.co/videos/preview/mixkit-woman-looking-directly-at-the-camera-40788-large.mp4',
        'pursed-kiss': 'https://assets.mixkit.co/videos/preview/mixkit-woman-blowing-a-kiss-to-the-camera-43224-large.mp4',
        'closed-smile': 'https://assets.mixkit.co/videos/preview/mixkit-girl-smiling-at-the-camera-in-close-up-34250-large.mp4',
        'grimace': 'https://assets.mixkit.co/videos/preview/mixkit-woman-winking-her-eye-43232-large.mp4',
        'big-smile': 'https://assets.mixkit.co/videos/preview/mixkit-woman-smiling-joyfully-42322-large.mp4',
        'furrow': 'https://assets.mixkit.co/videos/preview/mixkit-face-of-a-woman-smiling-close-up-39958-large.mp4'
      },
      after: {
        'at-rest': 'https://assets.mixkit.co/videos/preview/mixkit-face-of-a-woman-smiling-close-up-39958-large.mp4',
        'pursed-kiss': 'https://assets.mixkit.co/videos/preview/mixkit-woman-blowing-a-kiss-to-the-camera-43224-large.mp4',
        'closed-smile': 'https://assets.mixkit.co/videos/preview/mixkit-girl-smiling-at-the-camera-in-close-up-34250-large.mp4',
        'grimace': 'https://assets.mixkit.co/videos/preview/mixkit-woman-winking-her-eye-43232-large.mp4',
        'big-smile': 'https://assets.mixkit.co/videos/preview/mixkit-woman-smiling-joyfully-42322-large.mp4',
        'furrow': 'https://assets.mixkit.co/videos/preview/mixkit-woman-looking-directly-at-the-camera-40788-large.mp4'
      }
    });
  };

  const handleReset = () => {
    setBeforePhotos({
      'front': null,
      '45-left': null,
      '45-right': null,
      'left': null,
      'right': null,
      '90-left': null,
      '90-right': null
    });
    setAfterPhotos({
      'front': null,
      '45-left': null,
      '45-right': null,
      'left': null,
      'right': null,
      '90-left': null,
      '90-right': null
    });
    setCapturedBeforeFacePositions({
      'front': null,
      '45-left': null,
      '45-right': null,
      'left': null,
      'right': null,
      '90-left': null,
      '90-right': null
    });
    setCapturedAfterFacePositions({
      'front': null,
      '45-left': null,
      '45-right': null,
      'left': null,
      'right': null,
      '90-left': null,
      '90-right': null
    });
    setBeforeCapturedReview(false);
    setStage('capture-before');
    setReviewTab('split');
    setOverlayOffset({ x: 0, y: 0 });
    setOverlayScale(1);
    setShowGhostOverlay(true);
    setRecordedVideos({
      before: {
        'at-rest': null,
        'pursed-kiss': null,
        'closed-smile': null,
        'grimace': null,
        'big-smile': null,
        'furrow': null
      },
      after: {
        'at-rest': null,
        'pursed-kiss': null,
        'closed-smile': null,
        'grimace': null,
        'big-smile': null,
        'furrow': null
      }
    });
    setVideoPhase('before');
    setVideoMode('grid');
    setActiveVideoExpression(null);
  };

  const handleRetakeBefore = () => {
    setBeforePhoto(null);
    setBeforeCapturedReview(false);
    setStage('capture-before');
    setCapturedBeforeFacePos(null);
    setCapturedAfterFacePos(null);
  };

  const handleRetakeAfter = () => {
    setAfterPhoto(null);
    setStage('capture-after');
    setCapturedAfterFacePos(null);
  };

  const toggleSyncPlayback = () => {
    const vBefore = compareBeforeRef.current;
    const vAfter = compareAfterRef.current;
    if (!vBefore || !vAfter) return;
    if (comparePlaying) {
      vBefore.pause();
      vAfter.pause();
      setComparePlaying(false);
    } else {
      vBefore.currentTime = 0;
      vAfter.currentTime = 0;
      vBefore.play().catch((err) => console.warn("Failed to play before video", err));
      vAfter.play().catch((err) => console.warn("Failed to play after video", err));
      setComparePlaying(true);
    }
  };

  useEffect(() => {
    if (videoMode !== 'compare') {
      setComparePlaying(false);
      if (compareBeforeRef.current) {
        compareBeforeRef.current.pause();
        compareBeforeRef.current.currentTime = 0;
      }
      if (compareAfterRef.current) {
        compareAfterRef.current.pause();
        compareAfterRef.current.currentTime = 0;
      }
    }
  }, [videoMode]);

  // Video recording control handlers
  const startVideoRecording = async () => {
    recordedBlobsRef.current = [];
    let stream = null;

    if (videoRef.current && videoRef.current.srcObject) {
      stream = videoRef.current.srcObject;
    } else {
      alert("Camera feed is not ready yet.");
      return;
    }

    let options = { mimeType: 'video/webm;codecs=vp9,opus' };
    if (!MediaRecorder.isTypeSupported(options.mimeType)) {
      options = { mimeType: 'video/webm;codecs=vp8,opus' };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = { mimeType: 'video/webm' };
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          options = { mimeType: 'video/mp4' };
        }
      }
    }

    try {
      mediaRecorderRef.current = new MediaRecorder(stream, options);
    } catch (e) {
      console.warn("Creating MediaRecorder with standard mimeType failed, falling back", e);
      try {
        mediaRecorderRef.current = new MediaRecorder(stream);
      } catch (err) {
        console.error("Failed to create MediaRecorder:", err);
        alert("Video recording is not supported on this browser/device.");
        return;
      }
    }

    mediaRecorderRef.current.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        recordedBlobsRef.current.push(event.data);
      }
    };

    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(recordedBlobsRef.current, { type: mediaRecorderRef.current.mimeType || 'video/webm' });
      const url = URL.createObjectURL(blob);
      createdUrlsRef.current.push(url);
      setTempVideoUrl(url);
      setVideoMode('preview');
    };

    setIsRecording(true);
    setRecordingSeconds(0);
    mediaRecorderRef.current.start();

    recordingTimerRef.current = setInterval(() => {
      setRecordingSeconds(prev => {
        if (prev >= 9) {
          stopVideoRecording();
          return 10;
        }
        return prev + 1;
      });
    }, 1000);
  };

  const stopVideoRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
  };

  // Revoke Blob URLs on unmount to prevent leaks
  useEffect(() => {
    return () => {
      createdUrlsRef.current.forEach(url => {
        if (url && url.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(url);
          } catch (e) {
            console.warn("Failed to revoke URL:", url, e);
          }
        }
      });
    };
  }, []);

  // Split-slider drag handlers using modern unified pointer events
  const handleSplitPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDraggingSplit(true);
    updateSplitPosition(e);
  };

  const handleSplitPointerMove = (e) => {
    if (!isDraggingSplit) return;
    updateSplitPosition(e);
  };

  const handleSplitPointerUp = (e) => {
    setIsDraggingSplit(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const updateSplitPosition = (e) => {
    if (!splitContainerRef.current) return;
    const rect = splitContainerRef.current.getBoundingClientRect();
    const relativeX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (relativeX / rect.width) * 100));
    setSplitOffset(percentage);
  };

  const handlePhotoPointerDown = (e) => {
    if (photoZoom <= 1) return;
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (err) { }
    setIsPanningPhoto(true);
    photoPanStart.current = { x: e.clientX - photoPan.x, y: e.clientY - photoPan.y };
  };

  const handlePhotoPointerMove = (e) => {
    if (!isPanningPhoto) return;
    const newX = e.clientX - photoPanStart.current.x;
    const newY = e.clientY - photoPanStart.current.y;
    const bound = (photoZoom - 1) * 200;
    const clampedX = Math.max(-bound, Math.min(bound, newX));
    const clampedY = Math.max(-bound, Math.min(bound, newY));
    setPhotoPan({ x: clampedX, y: clampedY });
  };

  const handlePhotoPointerUp = (e) => {
    setIsPanningPhoto(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (err) { }
  };

  const handleDownloadComparison = () => {
    logApiCall('GET', '/api/comparison/download-comp-01', 200, { before: beforePhoto?.substring(0, 40), after: afterPhoto?.substring(0, 40) });
    const link = document.createElement('a');
    link.href = afterPhoto || beforePhoto;
    link.download = 'salus_before_after_comparison.png';
    link.click();
    alert("Downloaded comparison image! (In a real EMR environment, this saves to the patient profile cloud)");
  };

  const handleShareComparison = () => {
    logApiCall('POST', '/api/comparison/share', 200, { comparisonId: 'comp-photo-01', patientId: '884-9020-MED' });
    navigator.clipboard.writeText('https://emr.salus.ai/comparison/comp-8849020-01').then(() => {
      alert("Secure EMR sharing link copied to clipboard!");
    });
  };

  const handleVideoSeek = (e) => {
    const val = parseFloat(e.target.value);
    setVideoTime(val);
    if (compareBeforeRef.current) compareBeforeRef.current.currentTime = val;
    if (compareAfterRef.current) compareAfterRef.current.currentTime = val;
    logApiCall('GET', `/api/comparison/seek?time=${val.toFixed(2)}`, 200, { seekTime: val });
  };

  const handleVideoTimeUpdate = (e) => {
    setVideoTime(e.target.currentTime);
    if (e.target.duration && e.target.duration !== videoDuration) {
      setVideoDuration(e.target.duration);
    }
  };

  const changePlaybackSpeed = (speed) => {
    setVideoSpeed(speed);
    if (compareBeforeRef.current) compareBeforeRef.current.playbackRate = speed;
    if (compareAfterRef.current) compareAfterRef.current.playbackRate = speed;
    logApiCall('GET', `/api/comparison/speed?rate=${speed}`, 200, { playbackRate: speed });
  };

  const handleCaptureWound = () => {
    const video = woundVideoRef.current;
    if (video && video.readyState >= 2) {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/png');
      setWoundCapturedImage(dataUrl);
    } else {
      setWoundCapturedImage(MOCK_BEFORE);
    }

    setWoundProcessing(true);
    logApiCall('POST', '/api/media/upload', 202, { type: 'wound_capture', marker: 'ArUco' });

    setTimeout(() => {
      logApiCall('GET', '/api/status/job-woundmatic-01', 200, { status: 'Segmenting Wound...' });

      setTimeout(() => {
        setWoundProcessing(false);
        setWoundMetrics({ length: 4.5, width: 3.1, area: 13.95 });
        logApiCall('GET', '/api/alignment/comp-wound-01', 200, { lengthCm: 4.5, widthCm: 3.1, areaSqCm: 13.95, ArUcoDetected: true });
      }, 900);
    }, 900);
  };

  const handleSavePair = () => {
    const linkBefore = document.createElement('a');
    linkBefore.href = beforePhoto;
    linkBefore.download = 'before_photo.png';
    linkBefore.click();

    setTimeout(() => {
      const linkAfter = document.createElement('a');
      linkAfter.href = afterPhoto;
      linkAfter.download = 'after_photo.png';
      linkAfter.click();
    }, 300);

    alert("Downloading photos! In a real EMR environment, this saves to the patient cloud profile.");
  }; return (
    <div className={`ghost-camera-container ${theme}-theme`}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleBeforeUpload}
        accept="image/*"
        style={{ display: 'none' }}
      />
      <input
        type="file"
        ref={afterFileInputRef}
        onChange={handleAfterUpload}
        accept="image/*"
        style={{ display: 'none' }}
      />
      <input
        type="file"
        ref={genericFileInputRef}
        onChange={(e) => {
          const file = e.target.files[0];
          if (!file) return;
          simulateUploadWorkflow(file, (dataUrl) => {
            alert(`File ${file.name} successfully uploaded and processed!`);
          });
        }}
        accept="image/*,video/*"
        style={{ display: 'none' }}
      />


      {/* 2. Primary Layout Switcher */}
      {activeTab === 'photos' && (
        /* Photo workflow */
        <>
          {(stage === 'capture-before' || stage === 'capture-after') ? (
            <div className="camera-dashboard">
              {/* Left: Interactive Viewport */}
              <div ref={viewportRef} className="viewport-container glass-panel">
                {beforeCapturedReview ? (
                  <div className="captured-review-view">
                    <img src={beforePhoto} alt="Captured Before Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />

                    {/* Floating overlay badges & buttons */}
                    <div className="review-badge-top">
                      ✨ Baseline Captured
                    </div>

                    <div className="review-actions-overlay">
                      <button
                        className="btn btn-danger"
                        onClick={() => {
                          setBeforePhoto(null);
                          setBeforeCapturedReview(false);
                          setStage('capture-before');
                        }}
                        style={{ minWidth: '120px' }}
                      >
                        ↺ Retake Before
                      </button>
                      <button
                        className="btn btn-success"
                        onClick={() => {
                          setBeforeCapturedReview(false);
                          setStage('capture-after');
                        }}
                        style={{ minWidth: '180px', padding: '0.75rem 1.5rem', fontSize: '1rem', fontWeight: 'bold' }}
                      >
                        Capture After Photo ➔
                      </button>
                    </div>
                  </div>
                ) : cameraError ? (
                  <div className="camera-fallback">
                    <span className="fallback-icon">📷</span>
                    <p style={{ color: 'var(--text-secondary)' }}>{cameraError}</p>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="btn btn-secondary" onClick={() => setSelectedDeviceId(cameraDevices[0]?.deviceId || '')}>Retry Camera</button>
                      <button className="btn btn-primary" onClick={handleLoadDemo}>Load Demo Profile</button>
                    </div>
                  </div>
                ) : stage === 'capture-after' && !beforePhoto ? (
                  /* Warning if trying to capture After without Before template */
                  <div className="camera-fallback" style={{ padding: '2rem' }}>
                    <span className="fallback-icon" style={{ fontSize: '2.5rem' }}>⚠️</span>
                    <h3 style={{ margin: '0.5rem 0 0.25rem 0' }}>Baseline Photo Required</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', maxWidth: '300px', margin: '0 0 1rem 0' }}>
                      Please capture the 'Before' photo first so it can be used as the alignment guide.
                    </p>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="btn btn-primary" onClick={() => setStage('capture-before')}>
                        ← Capture Before
                      </button>
                      <button className="btn btn-secondary" onClick={triggerBeforeUpload}>
                        Upload File
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {cameraLoading && (
                      <div className="camera-fallback" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 50, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: 'var(--bg-primary)' }}>
                        <div className="spinner" style={{
                          width: '40px',
                          height: '40px',
                          border: '3px solid rgba(255,255,255,0.1)',
                          borderTop: '3px solid var(--accent-color)',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '1rem' }}>Initializing video stream...</p>
                      </div>
                    )}



                    {/* Hidden raw video element supplying stream frames */}
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        pointerEvents: 'none',
                        zIndex: 1,
                        opacity: 0
                      }}
                    />

                    {/* Processed live viewport canvas */}
                    <canvas
                      ref={canvasRef}
                      className={`webcam-feed ${mirrorCamera ? '' : 'no-mirror'}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        zIndex: 2
                      }}
                    />





                    {stage === 'capture-after' && beforePhoto && showGhostOverlay && (
                      <img
                        src={beforePhoto}
                        alt="Before capture overlay"
                        className="ghost-overlay"
                        style={{
                          opacity: ghostOpacity,
                          pointerEvents: 'none',
                          transform: `translate(0px, 0px) scale(1) ${mirrorCamera ? 'scaleX(-1)' : ''}`,
                        }}
                      />
                    )}

                    {/* ── Face Angle Guide Overlay ── */}
                    {showSilhouette && stage !== 'capture-after' && (
                      <FaceAngleGuide pose={pose} showGrid={showGrid} />
                    )}

                    {/* ── Auto Capture Alignment HUD ── */}
                    {autoCaptureEnabled && stage === 'capture-after' && (
                      <div className="alignment-hud-overlay">
                        {(() => {
                          const target = capturedBeforeFacePos || { x: 200, y: 150 };

                          const dist = Math.sqrt(
                            Math.pow(facePosition.x - target.x, 2) +
                            Math.pow(facePosition.y - target.y, 2)
                          );
                          const centroidScore = Math.max(0, 1 - dist / 70) * 100;

                          let poseScore = 100;
                          if (beforePhotoDataRef.current && liveFrameDataRef.current) {
                            poseScore = calculateNormalizedSimilarity(
                              liveFrameDataRef.current,
                              beforePhotoDataRef.current,
                              facePosition,
                              capturedBeforeFacePos,
                              pose,
                              mirrorCamera
                            );
                          }

                          const matchPercent = Math.round(centroidScore * 0.4 + poseScore * 0.6);
                          const isAligned = isFaceDetected && matchPercent >= 90;
                          const progress = isAligned ? Math.min(100, Math.round((alignmentTime / 1000) * 100)) : 0;

                          return (
                            <>
                              {/* Central alignment guide target ring */}
                              <div
                                className={`alignment-target-ring ${isAligned ? 'aligned' : ''}`}
                                style={{
                                  left: `${target.x / 400 * 100}%`,
                                  top: `${target.y / 300 * 100}%`,
                                }}
                              >
                                {isAligned && (
                                  <svg className="alignment-progress-svg" viewBox="0 0 36 36">
                                    <circle className="bg" cx="18" cy="18" r="16" />
                                    <circle
                                      className="bar"
                                      cx="18"
                                      cy="18"
                                      r="16"
                                      pathLength="100"
                                      strokeDasharray="100"
                                      strokeDashoffset={100 - progress}
                                    />
                                  </svg>
                                )}
                                <div className="alignment-dot-inner" />
                                <span className="ring-label">Target</span>
                              </div>

                              {/* Live face position ring (only show separately if not aligned yet and face detected) */}
                              {!isAligned && isFaceDetected && (
                                <div
                                  className="alignment-live-ring"
                                  style={{
                                    left: `${facePosition.x / 400 * 100}%`,
                                    top: `${facePosition.y / 300 * 100}%`,
                                  }}
                                >
                                  <div className="live-dot-inner" />
                                  <span className="ring-label live">You</span>
                                </div>
                              )}

                              {/* Floating status label */}
                              <div className={`alignment-status-card ${isAligned ? 'active' : ''}`}>
                                {!isFaceDetected ? (
                                  <>
                                    <span className="dot" style={{ background: '#ef4444', boxShadow: '0 0 6px #ef4444' }}></span>
                                    <span>No face detected. Look directly at camera.</span>
                                  </>
                                ) : isAligned ? (
                                  <>
                                    <span className="pulse-dot"></span>
                                    <span>Aligned! Hold Still ({Math.max(0, Math.ceil((1000 - alignmentTime) / 100) / 10).toFixed(1)}s)</span>
                                  </>
                                ) : (
                                  <>
                                    <span className="dot"></span>
                                    <span>{matchPercent}% Aligned (Need 90% to auto-snap)</span>
                                  </>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}

                    <div className="viewport-hud-right">

                      {cameraDevices.length > 1 && (
                        <button
                          className="hud-btn"
                          onClick={() => {
                            const currentIndex = cameraDevices.findIndex(d => d.deviceId === selectedDeviceId);
                            const nextIndex = (currentIndex + 1) % cameraDevices.length;
                            setSelectedDeviceId(cameraDevices[nextIndex].deviceId);
                          }}
                          title="Flip Video Camera Source"
                        >
                          🔄
                        </button>
                      )}
                      <div className="shutter-hud-container">
                        <button
                          className={`hud-shutter-btn ${stage === 'capture-after' ? 'after-mode' : ''}`}
                          onClick={handleCapture}
                          title={stage === 'capture-before' ? "Capture Baseline Before" : "Capture Alignment After"}
                        >
                          <div className="hud-shutter-inner"></div>
                        </button>
                      </div>
                    </div>



                    <div className={`shutter-flash ${triggerFlash ? 'flash' : ''}`} />
                  </>
                )}
              </div>


              {/* Right: Sidebar Control Panel */}
              {false && (
                <div className="sidebar-panel glass-panel" style={{ position: 'relative' }}>
                  {beforeCapturedReview && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: theme === 'light' ? 'rgba(255, 255, 255, 0.75)' : 'rgba(15, 23, 42, 0.75)',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      zIndex: 60,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '1.5rem',
                      textAlign: 'center',
                      borderRadius: '16px'
                    }}>
                      <span style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📸</span>
                      <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-primary)' }}>Baseline Saved</h4>
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0 0 1.25rem 0', lineHeight: '1.4' }}>
                        The "Before" image has been stored. Click "Capture After" to start the alignment guide.
                      </p>
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          setBeforeCapturedReview(false);
                          setStage('capture-after');
                        }}
                        style={{ width: '100%' }}
                      >
                        Capture After Photo
                      </button>
                    </div>
                  )}

                  <div className="panel-section">
                    <h3 className="panel-title">Capture Phase</h3>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                      {stage === 'capture-before'
                        ? "Align the silhouette guides to snap the baseline 'Before' photo."
                        : "Position yourself against the transparent shadow overlay to capture the 'After' photo."
                      }
                    </p>
                  </div>

                  {cameraDevices.length > 1 && (
                    <div className="panel-section">
                      <label className="panel-title">Video Input</label>
                      <select
                        value={selectedDeviceId}
                        onChange={(e) => setSelectedDeviceId(e.target.value)}
                        className="camera-select"
                      >
                        {cameraDevices.map((device, idx) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Camera ${idx + 1}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="panel-section">
                    <h3 className="panel-title">Camera Modifiers</h3>
                    <div className="toggle-item">
                      <span className="toggle-label">Mirror Viewfinder</span>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={mirrorCamera}
                          onChange={(e) => setMirrorCamera(e.target.checked)}
                        />
                        <span className="slider-toggle"></span>
                      </label>
                    </div>
                    <div className="toggle-item">
                      <span className="toggle-label">Anatomical Silhouette</span>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={showSilhouette}
                          onChange={(e) => setShowSilhouette(e.target.checked)}
                        />
                        <span className="slider-toggle"></span>
                      </label>
                    </div>
                    {showSilhouette && (
                      <div className="toggle-item">
                        <span className="toggle-label">Alignment Grid Lines</span>
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={showGrid}
                            onChange={(e) => setShowGrid(e.target.checked)}
                          />
                          <span className="slider-toggle"></span>
                        </label>
                      </div>
                    )}
                  </div>

                  <div className="panel-section">
                    <h3 className="panel-title">AI Clinic Tools</h3>
                    <div className="toggle-item">
                      <span className="toggle-label">Live Face Lock HUD</span>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={liveFaceTracking}
                          onChange={(e) => setLiveFaceTracking(e.target.checked)}
                        />
                        <span className="slider-toggle"></span>
                      </label>
                    </div>
                    <div className="toggle-item">
                      <span className="toggle-label">AI Studio Background</span>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={aiBackgroundRemoval}
                          onChange={(e) => setAiBackgroundRemoval(e.target.checked)}
                        />
                        <span className="slider-toggle"></span>
                      </label>
                    </div>
                    <div className="toggle-item">
                      <span className="toggle-label">Auto-Capture on Alignment</span>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={autoCaptureEnabled}
                          onChange={(e) => setAutoCaptureEnabled(e.target.checked)}
                        />
                        <span className="slider-toggle"></span>
                      </label>
                    </div>
                  </div>

                  {stage === 'capture-after' && beforePhoto && (
                    <div className="panel-section">
                      <div className="slider-group">
                        <div className="slider-header">
                          <span>Ghost Overlay Opacity</span>
                          <span>{Math.round(ghostOpacity * 100)}%</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={ghostOpacity}
                          onChange={(e) => setGhostOpacity(parseFloat(e.target.value))}
                          className="custom-range"
                        />
                      </div>
                    </div>
                  )}

                  {!cameraError && !(stage === 'capture-after' && !beforePhoto) && (
                    <div className="panel-section" style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                      <div className="shutter-btn-container">
                        <button
                          onClick={handleCapture}
                          className={`shutter-outer ${stage === 'capture-after' ? 'after-mode' : ''}`}
                          title={stage === 'capture-before' ? "Capture Before" : "Capture After"}
                        >
                          <div className="shutter-inner"></div>
                        </button>
                      </div>
                      <div style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                        {stage === 'capture-before' ? "SNAP BEFORE PHOTO" : "SNAP AFTER PHOTO"}
                      </div>
                    </div>
                  )}

                  <div className="panel-section">
                    <h3 className="panel-title">Current Captures</h3>
                    <div className="thumbnails-container">
                      <div
                        className={`thumb-box clickable ${stage === 'capture-after' ? 'active' : ''}`}
                        onClick={beforePhoto ? handleUseAsGhostGuide : triggerBeforeUpload}
                        title={beforePhoto ? "Click to use as camera alignment guide" : "Click to upload Before photo"}
                      >
                        {beforePhoto ? (
                          <>
                            <img src={beforePhoto} className="thumb-img" alt="Captured Before" />
                            <span className="thumb-label">Before</span>
                            <div className="thumb-overlay-hint">
                              <span>Align 📸</span>
                            </div>
                            <button
                              className="thumb-delete"
                              onClick={(e) => { e.stopPropagation(); handleRetakeBefore(); }}
                              title="Retake Before"
                            >
                              ×
                            </button>
                          </>
                        ) : (
                          <div className="thumb-placeholder">
                            <span style={{ fontSize: '1.25rem' }}>+</span>
                            <span style={{ fontSize: '0.65rem' }}>Upload Before</span>
                          </div>
                        )}
                      </div>

                      <div
                        className={`thumb-box ${afterPhoto ? 'clickable' : ''}`}
                        onClick={afterPhoto ? () => { setStage('capture-after'); } : null}
                        title={afterPhoto ? "Click to retake After photo" : ""}
                      >
                        {afterPhoto ? (
                          <>
                            <img src={afterPhoto} className="thumb-img" alt="Captured After" />
                            <span className="thumb-label">After</span>
                            <button
                              className="thumb-delete"
                              onClick={(e) => { e.stopPropagation(); handleRetakeAfter(); }}
                              title="Retake After"
                            >
                              ×
                            </button>
                          </>
                        ) : (
                          <div className="thumb-placeholder">
                            <span style={{ fontSize: '1.25rem' }}>📸</span>
                            <span style={{ fontSize: '0.65rem' }}>Pending After</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {beforePhoto && afterPhoto && (
                      <button className="btn btn-success" onClick={() => setStage('review')}>
                        📊 Compare & Review Results
                      </button>
                    )}
                    <button className="btn btn-secondary" onClick={handleLoadDemo}>
                      Load Demo Client Photos
                    </button>
                    <button className="btn btn-danger" onClick={handleReset}>
                      Reset Camera
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Review Mode */
            (() => {
              const pctX = autoAlignPostCapture && capturedBeforeFacePos && capturedAfterFacePos
                ? ((capturedBeforeFacePos.x - capturedAfterFacePos.x) / 400) * 100
                : 0;
              const pctY = autoAlignPostCapture && capturedBeforeFacePos && capturedAfterFacePos
                ? ((capturedBeforeFacePos.y - capturedAfterFacePos.y) / 300) * 100
                : 0;

              return (
                <div className="review-dashboard">
                  {/* Pose Selector Strip in Review Mode */}
                  <div className="pose-selector-strip" style={{ marginBottom: '1.5rem', width: '100%' }}>
                    <span className="pose-strip-label">Face Angle</span>
                    <div className="pose-strip-btns">
                      {FACE_POSES.map(p => (
                        <button
                          key={p.id}
                          className={`pose-btn ${pose === p.id ? 'active' : ''}`}
                          onClick={() => handlePoseChange(p.id)}
                          title={p.label}
                        >
                          <span className="pose-btn-icon">{p.icon}</span>
                          <span className="pose-btn-label">{p.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="review-tools">
                    <button
                      onClick={() => setReviewTab('split')}
                      className={`tool-tab ${reviewTab === 'split' ? 'active' : ''}`}
                    >
                      50/50 Split Slider
                    </button>
                    <button
                      onClick={() => setReviewTab('side-by-side')}
                      className={`tool-tab ${reviewTab === 'side-by-side' ? 'active' : ''}`}
                    >
                      Side-by-Side
                    </button>
                    <button
                      onClick={() => setReviewTab('blend')}
                      className={`tool-tab ${reviewTab === 'blend' ? 'active' : ''}`}
                    >
                      Blend / Fade
                    </button>
                  </div>

                  {reviewTab === 'split' && (
                    <div
                      ref={splitContainerRef}
                      className="split-viewer-container"
                      onPointerDown={handleSplitPointerDown}
                      onPointerMove={handleSplitPointerMove}
                      onPointerUp={handleSplitPointerUp}
                      style={{ cursor: 'ew-resize' }}
                    >
                      <div className="split-label-indicator before">Before</div>
                      <div className="split-label-indicator after">After</div>

                      <div className="split-image-layer">
                        <img src={beforePhoto} alt="Review Before" />
                      </div>

                      <div
                        className="split-image-layer split-after-layer"
                        style={{ clipPath: `inset(0 0 0 ${splitOffset}%)` }}
                      >
                        <img
                          src={afterPhoto}
                          alt="Review After"
                          style={{
                            transform: `translate(${pctX}%, ${pctY}%) scale(1.0)`,
                            transformOrigin: 'center center'
                          }}
                        />
                      </div>

                      <div className="split-divider-line" style={{ left: `${splitOffset}%` }} />
                      <div className="split-divider-handle" style={{ left: `${splitOffset}%` }}>
                        ⇄
                      </div>
                    </div>
                  )}

                  {reviewTab === 'side-by-side' && (
                    <div className="side-by-side-container">
                      <div
                        className="side-card clickable"
                        onClick={handleUseAsGhostGuide}
                        title="Click to align live camera to this photo"
                      >
                        <div className="side-card-header">
                          <span>Baseline (Before)</span>
                          <span className="badge-guide">📸 Click to Align</span>
                        </div>
                        <div className="side-card-body relative-body">
                          <img src={beforePhoto} alt="Side by Side Before" />
                          <div className="side-card-overlay">
                            <span className="overlay-icon">📸</span>
                            <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Align & Capture After</span>
                          </div>
                        </div>
                      </div>

                      <div
                        className="side-card clickable"
                        onClick={() => setStage('capture-after')}
                        title="Click to retake After photo"
                      >
                        <div className="side-card-header">
                          <span>Post-Treatment (After)</span>
                          <span className="badge-guide">🔄 Click to Retake</span>
                        </div>
                        <div className="side-card-body relative-body">
                          <img src={afterPhoto} alt="Side by Side After" />
                          <div className="side-card-overlay">
                            <span className="overlay-icon">🔄</span>
                            <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Retake After Photo</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {reviewTab === 'blend' && (
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                      <div className="blend-viewer-container">
                        <img src={beforePhoto} alt="Blend Before" className="blend-before" />
                        <img
                          src={afterPhoto}
                          alt="Blend After"
                          className="blend-after"
                          style={{
                            opacity: blendOpacity,
                            transform: `translate(${pctX}%, ${pctY}%) scale(1.0)`,
                            transformOrigin: 'center center'
                          }}
                        />
                      </div>
                      <div className="slider-group" style={{ width: '100%', maxWidth: '500px' }}>
                        <div className="slider-header">
                          <span>Before (0%)</span>
                          <span>Blend Shift: {Math.round(blendOpacity * 100)}%</span>
                          <span>After (100%)</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.01"
                          value={blendOpacity}
                          onChange={(e) => setBlendOpacity(parseFloat(e.target.value))}
                          className="custom-range"
                        />
                      </div>
                    </div>
                  )}

                  {capturedBeforeFacePos && capturedAfterFacePos && (
                    <div className="toggle-item" style={{ maxWidth: '320px', margin: '1rem auto 0 auto', background: 'var(--bg-secondary)', padding: '0.5rem 1rem', borderRadius: '20px', border: '1px solid var(--border-color)', gap: '2rem' }}>
                      <span className="toggle-label" style={{ fontWeight: 600 }}>AI Post-Capture Auto-Align</span>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={autoAlignPostCapture}
                          onChange={(e) => setAutoAlignPostCapture(e.target.checked)}
                        />
                        <span className="slider-toggle"></span>
                      </label>
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', marginTop: '1.5rem', width: '100%' }}>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', background: 'rgba(255,255,255,0.02)', padding: '0.5rem 1rem', borderRadius: '20px', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span>💡</span>
                      <span><strong>EMR Alignment Tip:</strong> Click the <strong>Baseline (Before)</strong> photo to project it as a camera shadow for precise overlay positioning.</span>
                    </div>
                    <div className="review-footer-buttons">
                      <button className="btn btn-secondary" onClick={handleReset}>
                        ↺ Start New Patient
                      </button>
                      <button className="btn btn-primary" onClick={handleSavePair}>
                        💾 Save Comparison Pair
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()
          )}
        </>
      )}

      {activeTab === 'videos' && (
        /* Video workflow */
        <div className="video-dashboard-content" style={{ width: '100%', maxWidth: '1200px' }}>
          {videoMode === 'grid' ? (
            <div className="video-grid-container">
              <div className="video-grid-header">
                <h2>Procedure Videos</h2>
                <p>Record both Before and After videos for each facial expression template.</p>
              </div>

              <div className="video-grid">
                {EXPRESSIONS.map((exp) => {
                  const beforeUrl = recordedVideos.before[exp.id];
                  const afterUrl = recordedVideos.after[exp.id];

                  let statusText = "Before Pending";
                  if (beforeUrl && !afterUrl) {
                    statusText = "Before Recorded • After Pending";
                  } else if (beforeUrl && afterUrl) {
                    statusText = "Both Videos Recorded";
                  }

                  return (
                    <div key={exp.id} className="video-card glass-panel">
                      <div className="video-card-header-info">
                        <h3>{exp.name}</h3>
                        <span className={`video-status-badge ${beforeUrl && afterUrl ? 'completed' : beforeUrl ? 'partial' : 'pending'}`}>
                          {statusText}
                        </span>
                      </div>

                      <div className="video-card-slots">
                        {/* Before Slot */}
                        <div
                          className={`video-sub-slot ${beforeUrl ? 'recorded' : 'pending'}`}
                          onClick={() => {
                            setActiveVideoExpression(exp.id);
                            setVideoPhase('before');
                            if (beforeUrl) {
                              setVideoMode('playback');
                            } else {
                              setVideoMode('recording');
                            }
                          }}
                        >
                          <span className="slot-title">Before</span>
                          <div className="slot-preview">
                            {beforeUrl ? (
                              <VideoThumbnail videoUrl={beforeUrl} />
                            ) : (
                              <div className="illustration-wrapper small-illust">
                                <ExpressionIllustration expression={exp.id} />
                              </div>
                            )}
                            {beforeUrl && (
                              <button
                                className="video-delete-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRecordedVideos(prev => ({
                                    ...prev,
                                    before: { ...prev.before, [exp.id]: null },
                                    after: { ...prev.after, [exp.id]: null }
                                  }));
                                }}
                                title="Delete Before Video"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        </div>

                        {/* After Slot */}
                        {beforeUrl && (
                          <div
                            className={`video-sub-slot ${afterUrl ? 'recorded' : 'pending'}`}
                            onClick={() => {
                              setActiveVideoExpression(exp.id);
                              setVideoPhase('after');
                              if (afterUrl) {
                                setVideoMode('playback');
                              } else {
                                setVideoMode('recording');
                              }
                            }}
                          >
                            <span className="slot-title">After</span>
                            <div className="slot-preview">
                              {afterUrl ? (
                                <VideoThumbnail videoUrl={afterUrl} />
                              ) : (
                                <div className="illustration-wrapper small-illust">
                                  <ExpressionIllustration expression={exp.id} />
                                </div>
                              )}
                              {afterUrl && (
                                <button
                                  className="video-delete-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setRecordedVideos(prev => ({
                                      ...prev,
                                      after: { ...prev.after, [exp.id]: null }
                                    }));
                                  }}
                                  title="Delete After Video"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {beforeUrl && afterUrl && (
                        <div style={{ padding: '0 1.25rem 1.25rem 1.25rem' }}>
                          <button
                            className="btn btn-primary compare-btn"
                            style={{ width: '100%' }}
                            onClick={() => {
                              setActiveVideoExpression(exp.id);
                              setVideoMode('compare');
                            }}
                          >
                            Compare Side-by-Side
                          </button>
                        </div>
                      )}


                    </div>
                  );
                })}
              </div>

              <div className="video-grid-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '2rem' }}>
                <button className="btn btn-secondary" onClick={handleLoadDemo}>
                  Load Demo Videos
                </button>
                <button
                  className="btn btn-success"
                  onClick={() => alert("Videos uploaded to clinical EMR successfully.")}
                  disabled={
                    Object.values(recordedVideos.before).filter(Boolean).length === 0 &&
                    Object.values(recordedVideos.after).filter(Boolean).length === 0
                  }
                >
                  Save & Upload Comparison Videos
                </button>
              </div>
            </div>
          ) : (
            <div className="camera-dashboard video-camera-dashboard">
              {/* Left: Viewport */}
              <div ref={viewportRef} className="viewport-container glass-panel">
                {videoMode === 'preview' ? (
                  <div className="captured-review-view">
                    <video
                      src={tempVideoUrl}
                      autoPlay
                      muted
                      loop
                      playsInline
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div className="review-badge-top">
                      🎥 Preview Video
                    </div>
                  </div>
                ) : (
                  <>
                    {cameraLoading && (
                      <div className="camera-fallback" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 50, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: 'var(--bg-primary)' }}>
                        <div className="spinner" style={{
                          width: '40px',
                          height: '40px',
                          border: '3px solid rgba(255,255,255,0.1)',
                          borderTop: '3px solid var(--accent-color)',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '1rem' }}>Initializing video stream...</p>
                      </div>
                    )}

                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        pointerEvents: 'none',
                        zIndex: 1,
                        opacity: 0
                      }}
                    />

                    <canvas
                      ref={canvasRef}
                      className={`webcam-feed ${mirrorCamera ? '' : 'no-mirror'}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        zIndex: 2
                      }}
                    />

                    {showSilhouette && (
                      <svg className="silhouette-svg" viewBox="0 0 400 300">
                        {showGrid && (
                          <>
                            <line x1="100" y1="0" x2="100" y2="300" className="grid-line" />
                            <line x1="300" y1="0" x2="300" y2="300" className="grid-line" />
                            <line x1="0" y1="75" x2="400" y2="75" className="grid-line" />
                            <line x1="0" y1="225" x2="400" y2="225" className="grid-line" />
                          </>
                        )}
                        <line x1="200" y1="0" x2="200" y2="300" className="center-line" />
                        <line x1="0" y1="150" x2="400" y2="150" className="center-line" />

                        <ellipse cx="200" cy="140" rx="75" ry="105" className="guide-contour" />
                        <line x1="140" y1="115" x2="260" y2="115" className="active-target" />
                        <circle cx="165" cy="115" r="4" className="active-target" />
                        <circle cx="235" cy="115" r="4" className="active-target" />
                        <line x1="190" y1="150" x2="210" y2="150" className="active-target" />
                        <line x1="200" y1="140" x2="200" y2="160" className="active-target" />

                        {activeVideoExpression === 'pursed-kiss' ? (
                          <circle cx="200" cy="207" r="12" className="active-target" strokeDasharray="3 3" />
                        ) : activeVideoExpression === 'closed-smile' ? (
                          <path d="M 175 198 Q 200 215 225 198" className="active-target" strokeWidth="2.5" />
                        ) : activeVideoExpression === 'grimace' ? (
                          <path d="M 170 205 Q 200 202 230 205 Q 200 212 170 205 Z" className="active-target" strokeWidth="2" strokeDasharray="2 2" />
                        ) : activeVideoExpression === 'big-smile' ? (
                          <path d="M 170 198 Q 200 220 230 198 Q 200 205 170 198 Z" className="active-target" strokeWidth="2.5" />
                        ) : activeVideoExpression === 'furrow' ? (
                          <>
                            <path d="M 175 208 Q 200 200 225 208" className="active-target" strokeWidth="2.5" />
                            <path d="M 145 108 L 175 116" className="active-target" strokeWidth="2.5" />
                            <path d="M 225 116 L 255 108" className="active-target" strokeWidth="2.5" />
                          </>
                        ) : (
                          <path d="M 175 204 Q 200 206 225 204" className="guide-contour" />
                        )}
                      </svg>
                    )}

                    {liveFaceTracking && (
                      <div
                        className="face-lock-hud"
                        style={{
                          left: `${facePosition.x / 400 * 100}%`,
                          top: `${facePosition.y / 300 * 100}%`,
                          transform: 'translate(-50%, -50%)',
                        }}
                      >
                        <div className="face-lock-corner corner-tl"></div>
                        <div className="face-lock-corner corner-tr"></div>
                        <div className="face-lock-corner corner-bl"></div>
                        <div className="face-lock-corner corner-br"></div>
                        <div className="face-lock-label">
                          <span className="live-indicator" style={{ background: '#10b981', boxShadow: '0 0 6px #10b981' }}></span>
                          Patient Head Lock
                        </div>
                      </div>
                    )}

                    {videoPhase === 'after' && recordedVideos.before[activeVideoExpression] && showGhostOverlay && (
                      <video
                        ref={overlayVideoRef}
                        src={recordedVideos.before[activeVideoExpression]}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="ghost-overlay"
                        onLoadedMetadata={(e) => {
                          e.target.play().catch(err => console.warn("Failed to auto-play overlay:", err));
                        }}
                        style={{
                          opacity: ghostOpacity,
                          pointerEvents: 'none',
                          transform: `translate(0px, 0px) scale(1) ${mirrorCamera ? 'scaleX(-1)' : ''}`,
                        }}
                      />
                    )}






                    {isRecording && (
                      <div className="recording-hud-indicator">
                        <span className="rec-dot"></span>
                        <span className="rec-text">  REC 00:{recordingSeconds < 10 ? `0${recordingSeconds}` : recordingSeconds} / 00:10 </span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Right: Sidebar Panel */}
              <div className="sidebar-panel glass-panel">
                <div className="panel-section">
                  <h3 className="panel-title">Video Capture</h3>
                  <h2>{EXPRESSIONS.find(e => e.id === activeVideoExpression)?.name}</h2>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                    Align the patient with the outline template. Ask them to perform the expression while recording.
                  </p>
                </div>

                {videoMode === 'recording' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', flex: 1 }}>
                    <div className="panel-section">
                      <h3 className="panel-title">Target Expression</h3>
                      <div style={{ background: 'var(--bg-tertiary)', borderRadius: '12px', padding: '1rem', display: 'flex', justifyContent: 'center' }}>
                        <div style={{ width: '120px', height: '132px' }}>
                          <ExpressionIllustration expression={activeVideoExpression} />
                        </div>
                      </div>
                    </div>

                    <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {isRecording ? (
                        <button className="btn btn-danger" style={{ padding: '1rem' }} onClick={stopVideoRecording}>
                          ■ Stop Recording
                        </button>
                      ) : (
                        <button className="btn btn-primary" style={{ padding: '1rem' }} onClick={startVideoRecording}>
                          ● Start Recording (Max 10s)
                        </button>
                      )}
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          stopVideoRecording();
                          setVideoMode('grid');
                          setActiveVideoExpression(null);
                        }}
                        disabled={isRecording}
                      >
                        ← Back to Expressions
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', flex: 1 }}>
                    <div className="panel-section">
                      <h3 className="panel-title">Recording Review</h3>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                        Save the recording or discard it to try again.
                      </p>
                    </div>

                    <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <button
                        className="btn btn-success"
                        style={{ padding: '1rem' }}
                        onClick={() => {
                          setRecordedVideos(prev => ({
                            ...prev,
                            [videoPhase]: {
                              ...prev[videoPhase],
                              [activeVideoExpression]: tempVideoUrl
                            }
                          }));
                          setTempVideoUrl(null);
                          if (videoPhase === 'before') {
                            setCapturedBeforeFacePos({ ...facePosRef.current });
                            setVideoPhase('after');
                            setVideoMode('recording');
                          } else {
                            setVideoMode('grid');
                            setActiveVideoExpression(null);
                          }
                        }}
                      >
                        ✓ Save Video
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => {
                          if (tempVideoUrl && tempVideoUrl.startsWith('blob:')) {
                            URL.revokeObjectURL(tempVideoUrl);
                          }
                          setTempVideoUrl(null);
                          setVideoMode('recording');
                        }}
                      >
                        ↺ Discard & Retake
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Playback Modal */}
      {videoMode === 'playback' && activeVideoExpression && (
        <div className="video-modal-overlay" onClick={() => setVideoMode('grid')}>
          <div className="video-modal-content glass-panel" onClick={(e) => e.stopPropagation()}>
            <div className="video-modal-header">
              <h3>{videoPhase === 'before' ? 'Before' : 'After'} Video: {EXPRESSIONS.find(e => e.id === activeVideoExpression)?.name}</h3>
              <button className="close-modal-btn" onClick={() => setVideoMode('grid')}>×</button>
            </div>
            <div className="video-modal-body">
              <video
                src={recordedVideos[videoPhase][activeVideoExpression]}
                controls
                autoPlay
                muted
                loop
                playsInline
                className="playback-video"
              />
            </div>
            <div className="video-modal-footer">
              <button
                className="btn btn-danger"
                onClick={() => {
                  setVideoMode('recording');
                }}
              >
                🔄 Retake Video
              </button>
              <button className="btn btn-secondary" onClick={() => setVideoMode('grid')}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Before Video Review before capturing After Video */}
      {videoMode === 'before-preview' && activeVideoExpression && (
        <div className="video-modal-overlay" onClick={() => setVideoMode('grid')}>
          <div className="video-modal-content glass-panel" onClick={(e) => e.stopPropagation()}>
            <div className="video-modal-header">
              <h3>Review Before: {EXPRESSIONS.find(e => e.id === activeVideoExpression)?.name}</h3>
              <button className="close-modal-btn" onClick={() => setVideoMode('grid')}>×</button>
            </div>
            <div className="video-modal-body">
              <video
                src={recordedVideos.before[activeVideoExpression]}
                controls
                autoPlay
                muted
                loop
                playsInline
                className="playback-video"
              />
            </div>
            <div className="video-modal-footer" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <button className="btn btn-secondary" onClick={() => setVideoMode('grid')}>
                ← Back
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setVideoMode('recording');
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}
              >
                ● Proceed to Record After
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comparison Modal */}
      {videoMode === 'compare' && activeVideoExpression && (
        <div className="video-modal-overlay" onClick={() => setVideoMode('grid')}>
          <div className="video-modal-content compare-modal-content glass-panel" onClick={(e) => e.stopPropagation()}>
            <div className="video-modal-header">
              <h3>Compare: {EXPRESSIONS.find(e => e.id === activeVideoExpression)?.name}</h3>
              <button className="close-modal-btn" onClick={() => setVideoMode('grid')}>×</button>
            </div>
            <div className="video-modal-body compare-modal-body">
              <div className="compare-video-column">
                <div className="compare-column-title">Before</div>
                <video
                  ref={compareBeforeRef}
                  src={recordedVideos.before[activeVideoExpression]}
                  muted
                  loop
                  playsInline
                  className="playback-video"
                />
              </div>
              <div className="compare-video-column">
                <div className="compare-column-title">After</div>
                <video
                  ref={compareAfterRef}
                  src={recordedVideos.after[activeVideoExpression]}
                  muted
                  loop
                  playsInline
                  className="playback-video"
                />
              </div>
            </div>
            <div className="video-modal-footer compare-modal-footer">
              <button
                className={`btn ${comparePlaying ? 'btn-danger' : 'btn-success'}`}
                onClick={toggleSyncPlayback}
                style={{ minWidth: '160px' }}
              >
                {comparePlaying ? '⏸ Pause Sync' : '▶ Play Sync'}
              </button>
              <button className="btn btn-secondary" onClick={() => setVideoMode('grid')}>
                Close Comparison
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3. Comparison Tab */}
      {activeTab === 'comparison' && (
        <div className="comparison-tab-container glass-panel animate-scaleUp" style={{ width: '100%', maxWidth: '1200px', padding: '1.5rem', boxSizing: 'border-box' }}>
          <div className="comparison-type-toggle" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.75rem' }}>
            <button
              className={`tool-tab ${comparisonMode === 'photos' ? 'active' : ''}`}
              onClick={() => { setComparisonMode('photos'); logApiCall('GET', '/api/comparison/photos-comparison', 200); }}
            >
              Photo Comparison
            </button>
            <button
              className={`tool-tab ${comparisonMode === 'videos' ? 'active' : ''}`}
              onClick={() => { setComparisonMode('videos'); logApiCall('GET', '/api/comparison/videos-comparison', 200); }}
            >
              Synchronized Video Comparison
            </button>
          </div>

          {comparisonMode === 'photos' ? (
            <div className="photo-comparison-viewer-block">
              <div className="advanced-photo-controls">
                <div className="photo-zoom-controls">
                  <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>🔍 Zoom / Pan:</span>
                  <button className="btn btn-secondary" style={{ padding: '0.2rem 0.45rem', fontSize: '0.7rem' }} onClick={() => setPhotoZoom(prev => Math.max(1, prev - 0.25))}>Zoom -</button>
                  <span style={{ fontSize: '0.8rem', fontWeight: 'bold', minWidth: '40px', textAlign: 'center' }}>{photoZoom.toFixed(2)}x</span>
                  <button className="btn btn-secondary" style={{ padding: '0.2rem 0.45rem', fontSize: '0.7rem' }} onClick={() => setPhotoZoom(prev => Math.min(3, prev + 0.25))}>Zoom +</button>
                  <button className="btn btn-secondary" style={{ padding: '0.2rem 0.45rem', fontSize: '0.7rem' }} onClick={() => { setPhotoZoom(1); setPhotoPan({ x: 0, y: 0 }); }}>Reset</button>
                </div>
                <div className="photo-action-buttons">
                  <button className="btn btn-secondary" style={{ fontSize: '0.7rem', padding: '0.35rem 0.75rem' }} onClick={() => setIsFullscreenPhoto(!isFullscreenPhoto)}>
                    {isFullscreenPhoto ? 'Exit Fullscreen' : '📺 Fullscreen View'}
                  </button>
                  <button className="btn btn-primary" style={{ fontSize: '0.7rem', padding: '0.35rem 0.75rem' }} onClick={handleDownloadComparison}>
                    💾 Download Image
                  </button>
                  <button className="btn btn-success" style={{ fontSize: '0.7rem', padding: '0.35rem 0.75rem' }} onClick={handleShareComparison}>
                    🔗 Share Comparison
                  </button>
                </div>
              </div>

              {beforePhoto && afterPhoto ? (
                <div className={`photo-viewports-wrapper ${isFullscreenPhoto ? 'fullscreen-overlay' : ''}`}>
                  {isFullscreenPhoto && (
                    <button className="close-modal-btn" style={{ position: 'absolute', right: '20px', top: '20px', zIndex: 999 }} onClick={() => setIsFullscreenPhoto(false)}>×</button>
                  )}
                  <div className="compare-photos-row">
                    <div className="compare-image-container" style={{ flex: 1, aspectRatio: '4/3', overflow: 'hidden', border: '1px solid var(--border-color)', borderRadius: '12px', background: '#000', position: 'relative' }}>
                      <div className="split-label-indicator before" style={{ zIndex: 10 }}>Before</div>
                      <img
                        src={beforePhoto}
                        alt="Before"
                        onPointerDown={handlePhotoPointerDown}
                        onPointerMove={handlePhotoPointerMove}
                        onPointerUp={handlePhotoPointerUp}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transform: `scale(${photoZoom}) translate(${photoPan.x}px, ${photoPan.y}px)`,
                          transformOrigin: 'center center',
                          cursor: photoZoom > 1 ? 'move' : 'default',
                          userSelect: 'none'
                        }}
                      />
                    </div>
                    <div className="compare-image-container" style={{ flex: 1, aspectRatio: '4/3', overflow: 'hidden', border: '1px solid var(--border-color)', borderRadius: '12px', background: '#000', position: 'relative' }}>
                      <div className="split-label-indicator after" style={{ zIndex: 10 }}>After</div>
                      <img
                        src={afterPhoto}
                        alt="After"
                        onPointerDown={handlePhotoPointerDown}
                        onPointerMove={handlePhotoPointerMove}
                        onPointerUp={handlePhotoPointerUp}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transform: `scale(${photoZoom}) translate(${photoPan.x}px, ${photoPan.y}px)`,
                          transformOrigin: 'center center',
                          cursor: photoZoom > 1 ? 'move' : 'default',
                          userSelect: 'none'
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '3rem 1rem', textAlign: 'center', background: 'rgba(255,255,255,0.01)', borderRadius: '16px', border: '1px dashed var(--border-color)' }}>
                  <span style={{ fontSize: '2rem' }}>📷</span>
                  <h3 style={{ margin: '0.5rem 0' }}>No Photos Captured</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '1.25rem' }}>Go to the "Photos" tab to capture before and after cosmetic treatment images.</p>
                  <button className="btn btn-primary" onClick={() => setActiveTab('photos')}>Capture Photos</button>
                </div>
              )}
            </div>
          ) : (
            <div className="video-comparison-viewer-block">
              <div className="advanced-video-controls">
                <div className="video-expression-controls">
                  <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Expression:</span>
                  <select
                    value={selectedVideoCompareExpression}
                    onChange={(e) => setSelectedVideoCompareExpression(e.target.value)}
                    className="camera-select"
                    style={{ minWidth: '160px', padding: '0.3rem 0.5rem', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', fontSize: '0.8rem' }}
                  >
                    {EXPRESSIONS.map(exp => (
                      <option key={exp.id} value={exp.id}>
                        {exp.name} {recordedVideos.before[exp.id] && recordedVideos.after[exp.id] ? '✓' : '(Missing)'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="video-speed-controls">
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Speed:</span>
                  {[0.5, 1, 1.5, 2].map(speed => (
                    <button
                      key={speed}
                      className={`btn btn-secondary ${videoSpeed === speed ? 'active' : ''}`}
                      style={{ padding: '0.15rem 0.45rem', fontSize: '0.7rem' }}
                      onClick={() => changePlaybackSpeed(speed)}
                    >
                      {speed}x
                    </button>
                  ))}
                  <button
                    className="btn btn-secondary"
                    style={{ fontSize: '0.7rem', padding: '0.35rem 0.75rem', marginLeft: '0.5rem' }}
                    onClick={() => setIsFullscreenVideoCompare(!isFullscreenVideoCompare)}
                  >
                    {isFullscreenVideoCompare ? 'Exit Fullscreen' : '📺 Fullscreen'}
                  </button>
                </div>
              </div>

              {recordedVideos.before[selectedVideoCompareExpression] && recordedVideos.after[selectedVideoCompareExpression] ? (
                <div className={`video-viewports-wrapper ${isFullscreenVideoCompare ? 'fullscreen-overlay' : ''}`} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {isFullscreenVideoCompare && (
                    <button className="close-modal-btn" style={{ position: 'absolute', right: '20px', top: '20px', zIndex: 999 }} onClick={() => setIsFullscreenVideoCompare(false)}>×</button>
                  )}
                  <div className="compare-videos-row">
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      <div className="compare-column-title" style={{ fontSize: '0.8rem' }}>Before (Baseline)</div>
                      <video
                        ref={compareBeforeRef}
                        src={recordedVideos.before[selectedVideoCompareExpression]}
                        muted
                        loop
                        playsInline
                        onTimeUpdate={handleVideoTimeUpdate}
                        style={{ width: '100%', aspectRatio: '4/3', borderRadius: '12px', background: '#000', objectFit: 'cover', border: '1px solid var(--border-color)' }}
                      />
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      <div className="compare-column-title" style={{ fontSize: '0.8rem' }}>After (Post-Treatment)</div>
                      <video
                        ref={compareAfterRef}
                        src={recordedVideos.after[selectedVideoCompareExpression]}
                        muted
                        loop
                        playsInline
                        style={{ width: '100%', aspectRatio: '4/3', borderRadius: '12px', background: '#000', objectFit: 'cover', border: '1px solid var(--border-color)' }}
                      />
                    </div>
                  </div>

                  <div style={{ background: 'var(--bg-secondary)', borderRadius: '12px', padding: '0.75rem', border: '1px solid var(--border-color)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <button
                        className={`btn ${comparePlaying ? 'btn-danger' : 'btn-success'}`}
                        onClick={toggleSyncPlayback}
                        style={{ minWidth: '100px', padding: '0.35rem 0.85rem', fontSize: '0.8rem' }}
                      >
                        {comparePlaying ? '⏸ Pause' : '▶ Play Sync'}
                      </button>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--accent-color)', fontFamily: 'monospace' }}>
                        Frame {Math.round(videoTime * 30)} ↔ Frame {Math.round(videoTime * 30)}
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{ fontSize: '0.7rem', fontFamily: 'monospace' }}>0.00s</span>
                      <input
                        type="range"
                        min="0"
                        max={videoDuration || 10}
                        step="0.05"
                        value={videoTime}
                        onChange={handleVideoSeek}
                        style={{ flex: 1, accentColor: 'var(--accent-color)', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.7rem', fontFamily: 'monospace' }}>{(videoDuration || 10).toFixed(2)}s</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '3rem 1rem', textAlign: 'center', background: 'rgba(255,255,255,0.01)', borderRadius: '16px', border: '1px dashed var(--border-color)' }}>
                  <span style={{ fontSize: '2rem' }}>🎥</span>
                  <h3 style={{ margin: '0.5rem 0' }}>No Videos Recorded for "{EXPRESSIONS.find(e => e.id === selectedVideoCompareExpression)?.name}"</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '1.25rem' }}>Go to the "Videos" tab to record both Before and After video clips.</p>
                  <button className="btn btn-primary" onClick={() => setActiveTab('videos')}>Record Videos</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 4. Analysis Tab */}
      {activeTab === 'analysis' && (
        <div className="analysis-tab-container glass-panel animate-scaleUp" style={{ width: '100%', maxWidth: '1200px', padding: '2rem', boxSizing: 'border-box' }}>
          <div className="analysis-row">
            {/* Patient Verification (Module 6) */}
            <div className="glass-panel analysis-card-left">
              <h3 style={{ margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                👤 Patient Biometric Verification
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '1.5rem 0', gap: '1rem' }}>
                <div className="radial-progress-score" style={{ position: 'relative', width: '120px', height: '120px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <svg width="120" height="120" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="8" />
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke={similarityScore >= 85 ? 'var(--success-color)' : '#f59e0b'}
                      strokeWidth="8"
                      strokeDasharray="314.16"
                      strokeDashoffset={314.16 - (314.16 * similarityScore) / 100}
                      strokeLinecap="round"
                      style={{ transition: 'stroke-dashoffset 0.3s ease, stroke 0.3s ease' }}
                    />
                  </svg>
                  <span style={{ position: 'absolute', fontSize: '1.5rem', fontWeight: 'bold' }}>{similarityScore.toFixed(1)}%</span>
                </div>

                <div style={{ textAlign: 'center' }}>
                  {similarityScore >= 85 ? (
                    <div style={{ color: 'var(--success-color)', fontWeight: 'bold', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.25rem', justifyContent: 'center' }}>
                      ✓ Patient Verified
                    </div>
                  ) : (
                    <div style={{ color: '#f59e0b', fontWeight: 'bold', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.25rem', justifyContent: 'center' }}>
                      ⚠️ Manual Review Required
                    </div>
                  )}
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '0.5rem', maxWidth: '250px' }}>
                    {similarityScore >= 85
                      ? 'Face match confidence is within secure clinical standards.'
                      : 'Biometric confidence is below threshold. Manual clinical sign-off required.'}
                  </p>
                </div>

                {/* Score slider for demo */}
                <div style={{ width: '100%', marginTop: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>
                    <span>Simulate Match Confidence:</span>
                    <span>{similarityScore.toFixed(0)}%</span>
                  </div>
                  <input
                    type="range"
                    min="50"
                    max="100"
                    step="0.5"
                    value={similarityScore}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      setSimilarityScore(val);
                      logApiCall('GET', `/api/patient/884-9020-MED?verifyScore=${val}`, 200, { verified: val >= 85, score: val });
                    }}
                    style={{ width: '100%', accentColor: 'var(--accent-color)' }}
                  />
                </div>
              </div>
            </div>

            {/* Alignment Score Dashboard (Module 7) */}
            <div className="glass-panel analysis-card-right">
              <h3 style={{ margin: '0 0 1rem 0' }}>📊 Feature Alignment Analysis</h3>

              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                    <th style={{ padding: '0.75rem 0.5rem' }}>Metric</th>
                    <th style={{ padding: '0.75rem 0.5rem' }}>Match Status</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: 'Head Rotation', score: 98, color: 'var(--success-color)' },
                    { name: 'Eye Position', score: 99, color: 'var(--success-color)' },
                    { name: 'Scale Match', score: 97, color: 'var(--success-color)' },
                    { name: 'Lighting Match', score: 92, color: 'var(--success-color)' },
                    { name: 'Overall Alignment Quality', score: 96, color: 'var(--accent-color)', bold: true }
                  ].map((m, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', fontWeight: m.bold ? 'bold' : 'normal' }}>
                      <td style={{ padding: '0.75rem 0.5rem' }}>{m.name}</td>
                      <td style={{ padding: '0.75rem 0.5rem', width: '50%' }}>
                        <div style={{ background: 'rgba(255,255,255,0.02)', width: '100%', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ background: m.color, width: `${m.score}%`, height: '100%', borderRadius: '4px' }}></div>
                        </div>
                      </td>
                      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: m.color }}>{m.score}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ display: 'flex', gap: '0.5rem', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '0.75rem', marginTop: '1.5rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                <span>🛡️</span>
                <span>All biometric vectors have been processed against FDA calibration targets and stored in Patient EHR profile ID <strong>884-9020-MED</strong>.</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 5. WoundMatic Capture (Module 8) */}
      {activeTab === 'woundmatic' && (
        <div className="woundmatic-dashboard glass-panel animate-scaleUp" style={{ width: '100%', maxWidth: '1200px', padding: '2rem', boxSizing: 'border-box' }}>
          <div className="woundmatic-header" style={{ marginBottom: '1.5rem' }}>
            <h2>WoundMatic Capture & Analysis</h2>
            <p>Position the ArUco marker next to the wound for calibration, then click capture.</p>
          </div>

          <div className="woundmatic-row">
            <div className="woundmatic-viewport-card">
              {woundProcessing ? (
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', zIndex: 30, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <div className="spinner" style={{ width: '40px', height: '40px', border: '3px solid rgba(255,255,255,0.1)', borderTop: '3px solid var(--accent-color)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                  <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Detecting ArUco Marker and segmenting wound boundary...</p>
                </div>
              ) : woundCapturedImage ? (
                <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                  <img src={woundCapturedImage} alt="Captured Wound" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <div style={{ position: 'absolute', border: '3.5px solid #10b981', left: '25%', top: '35%', width: '15%', height: '15%', borderRadius: '4px', boxShadow: '0 0 12px rgba(16,185,129,0.5)', zIndex: 10 }}>
                    <span style={{ position: 'absolute', background: '#10b981', color: '#fff', fontSize: '0.6rem', fontWeight: 'bold', padding: '1px 4px', top: '-18px', left: '-3.5px', borderRadius: '3px', whiteSpace: 'nowrap' }}>ArUco Marker</span>
                  </div>
                  <div style={{ position: 'absolute', border: '3.5px solid #ef4444', left: '48%', top: '25%', width: '30%', height: '45%', borderRadius: '8px', boxShadow: '0 0 12px rgba(239,68,68,0.5)', zIndex: 10 }}>
                    <span style={{ position: 'absolute', background: '#ef4444', color: '#fff', fontSize: '0.6rem', fontWeight: 'bold', padding: '1px 4px', top: '-18px', left: '-3.5px', borderRadius: '3px', whiteSpace: 'nowrap' }}>Wound Boundary</span>
                  </div>
                </div>
              ) : (
                <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                  {woundCameraLoading && (
                    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 10, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: 'var(--bg-primary)' }}>
                      <div className="spinner" style={{ width: '40px', height: '40px', border: '3px solid rgba(255,255,255,0.1)', borderTop: '3px solid var(--accent-color)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '1rem' }}>Accessing video feed...</p>
                    </div>
                  )}
                  {woundCameraError ? (
                    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '2rem', textAlign: 'center' }}>
                      <span style={{ fontSize: '2.5rem' }}>⚠️</span>
                      <p style={{ color: 'var(--text-secondary)', marginTop: '1rem' }}>{woundCameraError}</p>
                      <button className="btn btn-primary" onClick={() => setWoundCapturedImage(MOCK_BEFORE)}>Load Fallback Frame</button>
                    </div>
                  ) : (
                    <>
                      <video ref={woundVideoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <canvas ref={woundCanvasRef} style={{ display: 'none' }} />
                      <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 5 }} viewBox="0 0 400 300">
                        <rect x="100" y="100" width="60" height="60" rx="4" fill="none" stroke="#10b981" strokeWidth="2" strokeDasharray="5 5" />
                        <text x="100" y="90" fill="#10b981" fontSize="8" fontWeight="bold">ArUco Zone</text>

                        <rect x="200" y="70" width="120" height="150" rx="8" fill="none" stroke="#ef4444" strokeWidth="2" strokeDasharray="5 5" />
                        <text x="200" y="60" fill="#ef4444" fontSize="8" fontWeight="bold">Wound Target</text>
                      </svg>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="woundmatic-metrics-card">
              <div className="glass-panel" style={{ padding: '1.5rem', background: 'var(--bg-tertiary)' }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>Wound Calibration</h3>
                {woundMetrics ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '0.35rem' }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Length:</span>
                      <strong style={{ fontSize: '0.95rem' }}>{woundMetrics.length} cm</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '0.35rem' }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Width:</span>
                      <strong style={{ fontSize: '0.95rem' }}>{woundMetrics.width} cm</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '0.35rem' }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Segmented Area:</span>
                      <strong style={{ fontSize: '1.1rem', color: '#ef4444' }}>{woundMetrics.area} cm²</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '0.35rem', opacity: 0.6 }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Depth (Future):</span>
                      <span style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>Pending sensor</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '0.35rem', opacity: 0.6 }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Volume (Future):</span>
                      <span style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>Pending 3D mesh</span>
                    </div>
                  </div>
                ) : (
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>Capture a photo using the live camera or upload a reference file to trigger automatic segmentation measurements.</p>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: 'auto' }}>
                {!woundCapturedImage ? (
                  <button className="btn btn-primary" onClick={handleCaptureWound} style={{ padding: '0.75rem' }} disabled={woundCameraLoading}>
                    📸 Capture & Segment Wound
                  </button>
                ) : (
                  <>
                    <button className="btn btn-secondary" onClick={() => { setWoundCapturedImage(null); setWoundMetrics(null); }}>
                      ↺ Discard & Recapture
                    </button>
                    <button className="btn btn-success" onClick={() => { logApiCall('POST', '/api/wound/save', 200, { metrics: woundMetrics }); alert('Wound report successfully uploaded to Sarah Jenkins EMR file!'); }}>
                      💾 Save Wound Report
                    </button>
                  </>
                )}
                <button className="btn btn-secondary" onClick={() => { genericFileInputRef.current.click(); }}>
                  📁 Upload File (JPG/PNG)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 6. Future Tab */}
      {activeTab === 'future' && (
        <div className="future-tab-container glass-panel animate-scaleUp" style={{ width: '100%', maxWidth: '1200px', padding: '3rem 1.5rem', boxSizing: 'border-box', textAlign: 'center' }}>
          <div className="face-mesh-wireframe" style={{ width: '180px', height: '180px', margin: '0 auto 2rem auto', border: '1px dashed var(--accent-color)', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', animation: 'spin 12s linear infinite', position: 'relative' }}>
            <div style={{ width: '140px', height: '140px', border: '1px dashed #a855f7', borderRadius: '50%' }}></div>
            <div style={{ width: '100px', height: '100px', border: '1.5px solid #6366f1', borderRadius: '50%', position: 'absolute' }}></div>
            <div style={{ position: 'absolute', width: '8px', height: '8px', background: '#3b82f6', borderRadius: '50%', top: '10px' }}></div>
            <div style={{ position: 'absolute', width: '8px', height: '8px', background: '#10b981', borderRadius: '50%', bottom: '10px' }}></div>
            <div style={{ position: 'absolute', width: '8px', height: '8px', background: '#ef4444', borderRadius: '50%', left: '10px' }}></div>
            <div style={{ position: 'absolute', width: '8px', height: '8px', background: '#e2e8f0', borderRadius: '50%', right: '10px' }}></div>
          </div>
          <h2>3D Face Analysis Pipeline</h2>
          <p style={{ color: 'var(--text-secondary)', maxWidth: '500px', margin: '0.5rem auto 1.5rem auto', lineHeight: '1.5' }}>
            Future deployment integrates WebGL / Three.js hardware acceleration to render stereoscopic point clouds and depth contours from structure-from-motion sensors.
          </p>
          <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--accent-color)', background: 'var(--accent-glow)', border: '1px solid rgba(99,102,241,0.2)', padding: '0.35rem 0.85rem', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Planned Module 9 Integration
          </span>
        </div>
      )}

      {/* Upload Progress Modal (Module 3) */}
      {uploadStatus !== 'idle' && (
        <div className="upload-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(5, 7, 12, 0.85)', backdropFilter: 'blur(8px)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div className="upload-modal-content glass-panel" style={{ width: '90%', maxWidth: '450px', padding: '2rem', boxSizing: 'border-box', textAlign: 'center', animation: 'scaleUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}>
            <h3 style={{ margin: '0 0 0.5rem 0' }}>Clinical Upload Center</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0 0 1.5rem 0', wordBreak: 'break-all' }}>
              Processing: <strong>{uploadFileName}</strong>
            </p>

            <div style={{ background: 'rgba(255,255,255,0.03)', height: '10px', borderRadius: '5px', width: '100%', overflow: 'hidden', marginBottom: '1.5rem', border: '1px solid var(--border-color)' }}>
              <div style={{ width: `${uploadProgress}%`, background: 'linear-gradient(90deg, #6366f1, #10b981)', height: '100%', borderRadius: '5px', transition: 'width 0.1s linear' }}></div>
            </div>

            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem 0', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {[
                { key: 'uploading', label: 'Uploading Media File...' },
                { key: 'facemesh', label: 'Processing Biometric Face Mesh...' },
                { key: 'verifying', label: 'Verifying Patient Identity (98.7%)...' },
                { key: 'stabilizing', label: uploadFileType.startsWith('video/') || uploadFileName.endsWith('.mp4') || uploadFileName.endsWith('.mov') ? 'Stabilizing Camera Shakes...' : 'Aligning Image Profiles...' },
                { key: 'preparing', label: 'Generating Side-by-Side Comparisons...' },
                { key: 'completed', label: 'Completed successfully ✓' }
              ].map(step => {
                let statusClass = 'pending';
                if (uploadStatus === step.key) {
                  statusClass = 'active';
                } else {
                  const list = ['uploading', 'facemesh', 'verifying', 'stabilizing', 'preparing', 'completed'];
                  const curIdx = list.indexOf(uploadStatus);
                  const stepIdx = list.indexOf(step.key);
                  if (curIdx > stepIdx) statusClass = 'completed';
                }

                let color = 'var(--text-secondary)';
                let icon = '○';
                if (statusClass === 'active') {
                  color = 'var(--accent-color)';
                  icon = '⏳';
                } else if (statusClass === 'completed') {
                  color = 'var(--success-color)';
                  icon = '✓';
                }

                return (
                  <li key={step.key} style={{ color, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', fontWeight: statusClass === 'active' ? 'bold' : 'normal' }}>
                    <span>{icon}</span>
                    <span>{step.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}


    </div>
  );
};
